import { LitElement, html, nothing } from 'lit';
import moment from 'moment';
import { buildCleanedTrack } from './path-cleaner.js';

/***********************************************************************************
 * <security-tracking-map>
 *
 * Modernized security tab map. Replaces the legacy SecurityMapView.
 *
 * Modes:
 *  - live:       single current position (GPS / WiFi / cellular fallback). Default.
 *                During an active theft / perimeter alert (tamper bits or health
 *                67 / 41) the view stays live and split-screen Street View opens
 *                automatically so live points update both panels.
 *  - historical: user-selected date range with path playback and tower overlay.
 *                Path can be shown cleaned (motion-gated + WiFi-anchored stops)
 *                or raw (every reported GPS/WiFi point).
 *
 * Data sources:
 *  - PropertyModel m/{device}/security/details  (fngetsecuritystate14 - currentlocation)
 *  - GET /r/p/securitytrackhistory              (points + towers + motions)
 ***********************************************************************************/

const MAP_ID = '6d8768db7cc07289e0c30303';
const THEFT_HEALTH_CODE = 67;
const PERIMETER_HEALTH_CODE = 41;
const MAX_POINT_MARKERS = 200;
const PLAYBACK_BASE_SECONDS = 60; // whole track plays in ~60s at 1x

const GPS_PATH_COLOR = '#2c7be5';
const WIFI_PATH_COLOR = '#9d4edd';

// Red dot matching .stm-playback-dot, used for historical points in street view.
const PANO_DOT_ICON = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22">' +
    '<circle cx="11" cy="11" r="7" fill="#d9534f" stroke="#fff" stroke-width="3"/></svg>'
);

function securityImg(src, size) {
    const img = document.createElement('img');
    img.src = `css/images/security/${src}`;
    img.width = size;
    img.height = size;
    return img;
}

// AdvancedMarkerElement anchors content at bottom-center, which shifts dot-style
// icons off their true coordinate (polylines use exact points). This wrapper
// recenters the icon on the coordinate so points line up with the path.
function centeredImg(src, size) {
    const wrap = document.createElement('div');
    wrap.style.cssText = `width:${size}px;height:${size}px;transform:translateY(50%);display:flex;`;
    wrap.appendChild(securityImg(src, size));
    return wrap;
}

// Compass bearing in degrees from one lat/lng to another (avoids needing the
// Google Maps geometry library, which index.html does not load).
function computeBearing(from, to) {
    const toRad = (d) => d * Math.PI / 180;
    const toDeg = (r) => r * 180 / Math.PI;
    const dLng = toRad(to.lng - from.lng);
    const lat1 = toRad(from.lat);
    const lat2 = toRad(to.lat);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

class SecurityTrackingMap extends LitElement {
    static properties = {
        device: { type: String },
        propertyname: { type: String },
        external: { type: Boolean }, // standalone shared-link page (no portal session)
        exttoken: { type: String },
        extadminid: { type: String },
        mode: { state: true },
        loadingTrack: { state: true },
        trackError: { state: true },
        showTowers: { state: true },
        isPlaying: { state: true },
        playbackIndex: { state: true },
        playbackPos: { state: true },
        playbackSpeed: { state: true },
        externallinkenabled: { state: true },
        locationSource: { state: true },
        hiddenCard: { state: true },
        truncatedTrack: { state: true },
        streetViewOn: { state: true },
        cellOnly: { state: true },
        panoAvailable: { state: true },
        pathMode: { state: true }, // 'cleaned' | 'raw'
    };

    createRenderRoot() {
        return this; // light DOM: reuse global portal css and allow calentim/jQuery
    }

    constructor() {
        super();
        this.mode = 'live';
        this.loadingTrack = false;
        this.trackError = null;
        this.showTowers = false;
        this.isPlaying = false;
        this.playbackIndex = 0;
        this.playbackSpeed = 1;
        this.playbackPos = 0; // ms offset into the track timeline
        this._pointTimes = [];
        this._trackStartMs = 0;
        this._trackDurationMs = 0;
        this._deviceTimeZoneId = null;
        this._deviceTimeFmt = null;
        this.hiddenCard = true;
        this.truncatedTrack = false;
        this.locationSource = null; // 'gps' | 'wifi' | 'cell' | null

        this.map = null;
        this.marker = null;
        this.userMarker = null;
        this.playbackMarker = null;
        this.geocoder = null;
        this.infoWindow = null;
        this.panorama = null; // currently visible (front) Street View
        this._panoA = null;
        this._panoB = null;
        this._panoFrontIsA = true;
        this._panoLoadSeq = 0;
        this._panoSwapTimer = null;
        this.svService = null;
        this.panoMarker = null;
        this.povMarker = null;
        this._povRotEl = null;
        this.streetViewOn = false;
        this._lastPanoIndex = -1;
        this._lastPanoUpdate = 0;
        this._lastPanoHeading = 0;
        this._lastPanoId = null;
        this._panoMarkerKind = 'gps';
        this._userScrubbed = false;
        this.panoAvailable = true;
        this.geoWatchId = null;
        this.geopoints = null;
        this.geolocationShort = '';
        this.modifiedView = false;
        this.firstView = true;
        this.latlng = null;

        this.points = [];      // active path (cleaned or raw), oldest -> newest
        this.rawPoints = [];
        this.cleanedPoints = [];
        this.stopClusters = []; // WiFi-anchored stops for cleaned view
        this.cleanStats = null;
        this.pathMode = 'cleaned';
        this.trackData = null;
        this.pathPolylines = [];
        this.pointMarkers = [];
        this.stopOverlays = [];
        this.towerOverlays = [];
        this.cellCircle = null;
        this.cellMarker = null;
        this.cellTowerData = null;
        this.cellLocation = null;
        this.cellOnly = false;

        this._alertWasActive = false;
        this.historicalRange = null;
        this.calendarSelect = null;
        this.incidents = []; // recent theft/perimeter/tamper incidents, newest first

        // Timeline zoom window (ms offsets into the track); null = full range
        this._winStartMs = null;
        this._winEndMs = null;
        this._tlDragging = false;
        this._tlSig = ''; // canvas redraw signature
        this._trim = null; // active trim-handle drag: { side: 'l'|'r', frac }
        this._winRedrawTimer = null; // debounced map refit after window changes
        this._resizeObserver = null;
        this._resizeDebounce = null;

        this.token = '';
        this.externallink = '';
        this.externallinkenabled = 0;
        this.external = false;
        this.exttoken = '';
        this.extadminid = '';

        this._rafId = null;
        this._lastFrameTs = null;
        this._infoMode = null; // 'live' | 'playback' | null — which popup is showing
        this._playbackInfoCloseTimer = null;
        this._playbackStep = this._playbackStep.bind(this);
        this._onModelChange = this._onModelChange.bind(this);
        this._onDelegatedClick = this._onDelegatedClick.bind(this);
    }

    // The info window content is a raw HTML string, so its copy button is wired
    // through delegation on the component (light DOM).
    _onDelegatedClick(e) {
        if (e.target.closest && e.target.closest('.stm-iw-copy')) {
            e.stopPropagation();
            if (this._infoMode === 'playback') this._copyPlaybackDetails();
            else this._copyLocationDetails();
        }
    }

    connectedCallback() {
        super.connectedCallback();
        // The external page assigns its own polling model before attach;
        // inside the portal we bind to the push-driven PropertyModel.
        if (!this.model && DplLink.getModelSet) {
            this.model = DplLink.getModelSet().getPropertyModel(this.propertyname);
        }
        if (this.model && this.model.on) {
            this.model.on('change', this._onModelChange);
        }
        this.addEventListener('click', this._onDelegatedClick);
        this._onThemeChanged = () => this._reinitMapForTheme();
        window.addEventListener('dpl-theme-changed', this._onThemeChanged);
        this._loadIncidents();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.model && this.model.off) {
            this.model.off('change', this._onModelChange);
        }
        this.removeEventListener('click', this._onDelegatedClick);
        if (this._onThemeChanged) {
            window.removeEventListener('dpl-theme-changed', this._onThemeChanged);
            this._onThemeChanged = null;
        }
        if (this._winRedrawTimer) {
            clearTimeout(this._winRedrawTimer);
            this._winRedrawTimer = null;
        }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this._resizeDebounce) {
            clearTimeout(this._resizeDebounce);
            this._resizeDebounce = null;
        }
        if (this._playbackInfoCloseTimer) {
            clearTimeout(this._playbackInfoCloseTimer);
            this._playbackInfoCloseTimer = null;
        }
        if (this._panoSwapTimer) {
            clearTimeout(this._panoSwapTimer);
            this._panoSwapTimer = null;
        }
        if (this.geoWatchId) {
            navigator.geolocation.clearWatch(this.geoWatchId);
            this.geoWatchId = null;
        }
        this._stopPlaybackLoop();
        if (this.calendarSelect && this.calendarSelect.destroy) {
            this.calendarSelect.destroy();
            this.calendarSelect = null;
        }
        this.map = null;
        this.panorama = null;
        this._panoA = null;
        this._panoB = null;
        this.panoMarker = null;
        this.povMarker = null;
        this._povRotEl = null;
        this.svService = null;
    }

    firstUpdated() {
        this._onModelChange();
        // Re-layout the map/panorama when the card is resized or the responsive
        // breakpoint flips the split orientation.
        const wrap = this.querySelector('.stm-map-wrap');
        if (wrap && typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => {
                if (this._resizeDebounce) clearTimeout(this._resizeDebounce);
                this._resizeDebounce = setTimeout(() => {
                    this._resizeDebounce = null;
                    if (typeof google === 'undefined') return;
                    if (this.map) google.maps.event.trigger(this.map, 'resize');
                    if (this._panoA) google.maps.event.trigger(this._panoA, 'resize');
                    if (this._panoB) google.maps.event.trigger(this._panoB, 'resize');
                }, 150);
            });
            this._resizeObserver.observe(wrap);
        }
    }

    /* ------------------------------------------------------------------ */
    /* Security state handling                                            */
    /* ------------------------------------------------------------------ */

    get status() {
        return this.model ? this.model.get('value') : null;
    }

    // Supports both the v14 `currentlocation` object and the legacy v13 `location` array.
    getCurrentLocation(status) {
        if (!status) return null;
        let loc = null;
        if (status.currentlocation && status.currentlocation.latitude !== undefined) {
            loc = status.currentlocation;
        } else if (Array.isArray(status.location) && status.location.length > 0 && status.location[0].latitude !== undefined) {
            loc = status.location[0];
        }
        return loc;
    }

    isLocationFresh(loc, status) {
        if (!loc) return false;
        return moment(loc.eventtime) > moment(status.celllocation && status.celllocation.lastrx).subtract(7, 'days');
    }

    getHealthCodes() {
        try {
            return DplLink.appView.deviceModel.get('value').healthcodes || [];
        } catch (e) {
            return [];
        }
    }

    isAlertActive(status) {
        const healthcodes = this.getHealthCodes();
        return status.tamper_theft === 1 ||
            status.tamper_perimeter === 1 ||
            healthcodes.includes(THEFT_HEALTH_CODE) ||
            healthcodes.includes(PERIMETER_HEALTH_CODE);
    }

    _onModelChange() {
        const status = this.status;
        if (!status) return;

        this._updateExternalLink(status);

        const loc = this.getCurrentLocation(status);
        const hasFreshLocation = this.isLocationFresh(loc, status);

        if (!hasFreshLocation && !status.cellstate) {
            this.hiddenCard = true;
            return;
        }
        if (this.hiddenCard && this.map) {
            // Card is becoming visible again; the map needs a resize kick.
            this.updateComplete.then(() => google.maps.event.trigger(this.map, 'resize'));
        }
        this.hiddenCard = false;

        if (!this.map) {
            // Map container renders once hiddenCard flips; init on next frame.
            this.updateComplete.then(() => this._initMap());
        }
        if (!this.map) return;

        // Active theft / perimeter breach: stay in live mode (points keep updating
        // the map and street view), just open split-screen street view once.
        // Street view needs a real GPS/WiFi fix, so skip it when cellular-only.
        const alertActive = this.isAlertActive(status);
        if (alertActive && !this._alertWasActive && this.mode === 'live' && !this.streetViewOn && hasFreshLocation) {
            this.streetViewOn = true;
            this._afterSplitToggle();
        }
        this._alertWasActive = alertActive;

        if (hasFreshLocation) {
            this.cellOnly = false;
            this.locationSource = loc.direction === 11 ? 'wifi' : 'gps';
            this._updateLiveMarker(loc, status);
        } else {
            if (!this.cellOnly) {
                // Entering cellular-only: the tower is the only location signal,
                // so the towers layer is on by default and street view (which
                // needs a GPS/WiFi fix) is unavailable.
                this.showTowers = true;
                if (this.streetViewOn) {
                    this.streetViewOn = false;
                    this._hideStreetViewPanos();
                    this._hidePovIndicator();
                    this._afterSplitToggle();
                }
            }
            this.cellOnly = true;
            this.locationSource = 'cell';
            if (this.marker) this.marker.map = null;
            this._showCurrentTower(status);
        }
    }

    /* ------------------------------------------------------------------ */
    /* Map initialization                                                 */
    /* ------------------------------------------------------------------ */

    _reinitMapForTheme() {
        if (!this.map) return;
        const mapEl = this.querySelector('.stm-map');
        let center = null, zoom = null;
        try { center = this.map.getCenter(); zoom = this.map.getZoom(); } catch (e) { /* noop */ }
        this.map = null;
        this.marker = null;
        if (mapEl) { mapEl.innerHTML = ''; }
        this._initMap();
        if (this.map) {
            if (center) this.map.setCenter(center);
            if (zoom != null) this.map.setZoom(zoom);
            try { this._onModelChange(); } catch (e) { /* refreshed on next model push */ }
        }
    }

    _initMap() {
        const mapEl = this.querySelector('.stm-map');
        if (this.map || !mapEl || typeof google === 'undefined') return;

        this.map = new google.maps.Map(mapEl, {
            center: { lat: 0, lng: 0 },
            zoom: 18,
            streetViewControl: false,
            gestureHandling: 'cooperative',
            zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP },
            mapTypeControl: true,
            mapTypeControlOptions: { mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE] },
            mapId: MAP_ID,
            colorScheme: (typeof DplLink !== 'undefined' && typeof DplLink.getMapColorScheme === 'function'
                ? DplLink.getMapColorScheme() : undefined)
        });

        this.geocoder = new google.maps.Geocoder();
        this.infoWindow = new google.maps.InfoWindow({ maxWidth: 280 });

        this.marker = new google.maps.marker.AdvancedMarkerElement({
            position: { lat: 0, lng: 0 },
            map: this.map,
            zIndex: 10000,
            title: 'Device Location',
            content: securityImg('atm_protector_marker.png', 36) // pin image: tip = coordinate
        });
        this.marker.addListener('click', () => {
            if (this.mode === 'historical') return;
            this._infoMode = 'live';
            this.infoWindow.open(this.map, this.marker);
        });

        this.svService = new google.maps.StreetViewService();
        this._initPanorama();

        // Recenter is a map task, so it lives on the map like Google's own controls.
        const centerBtn = document.createElement('button');
        centerBtn.type = 'button';
        centerBtn.className = 'stm-map-center-btn';
        centerBtn.title = 'Center the map on the device';
        centerBtn.innerHTML =
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">' +
            '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="1.5" fill="#555"/>' +
            '<line x1="12" y1="1" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="23"/>' +
            '<line x1="1" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="23" y2="12"/></svg>';
        centerBtn.addEventListener('click', () => this.onCenter());
        this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(centerBtn);

        this.map.addListener('drag', () => {
            this.modifiedView = true;
        });

        if (navigator.geolocation) {
            this._initUserMarker();
        }

        this._initCalendar();
        this._onModelChange();
    }

    _initUserMarker() {
        this.userMarker = new google.maps.marker.AdvancedMarkerElement({
            position: { lat: 0, lng: 0 },
            title: 'Your Location',
            content: centeredImg('location.png', 20)
        });

        navigator.geolocation.getCurrentPosition((position) => {
            this.userMarker.position = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.userMarker.map = this.map;
            this.geoWatchId = navigator.geolocation.watchPosition((posl) => {
                this.userMarker.position = { lat: posl.coords.latitude, lng: posl.coords.longitude };
            });
        }, (error) => {
            if (error.code !== 1) { // 1 = user denied geolocation
                console.error('Error getting User Geo: ', error);
            }
        }, { maximumAge: 60000, timeout: 10000, enableHighAccuracy: true });
    }

    /* ------------------------------------------------------------------ */
    /* Street View (split panel)                                          */
    /* ------------------------------------------------------------------ */

    _initPanorama() {
        const elA = this.querySelector('.stm-pano-a');
        const elB = this.querySelector('.stm-pano-b');
        if (this.panorama || !elA || !elB) return;

        const opts = {
            addressControl: false,
            enableCloseButton: false,
            linksControl: false,
            panControl: false,
            zoomControl: false,
            fullscreenControl: false,
            visible: false,
            pov: { heading: 0, pitch: 0 }
        };
        // Dual buffers: load the next Street View into the back pane, then
        // crossfade so playback never flashes black between tiles.
        this._panoA = new google.maps.StreetViewPanorama(elA, opts);
        this._panoB = new google.maps.StreetViewPanorama(elB, Object.assign({}, opts));
        this._panoFrontIsA = true;
        this.panorama = this._panoA;

        // Bind the front panorama to the map so the pegman uses our split panel.
        this.map.setStreetView(this.panorama);
        const onVisible = () => {
            if (this.panorama && this.panorama.getVisible() && !this.streetViewOn) {
                this.streetViewOn = true;
                this._afterSplitToggle();
            }
            this._updatePovIndicator();
        };
        this._panoA.addListener('visible_changed', onVisible);
        this._panoB.addListener('visible_changed', onVisible);
        this._panoA.addListener('pov_changed', () => this._updatePovIndicator());
        this._panoB.addListener('pov_changed', () => this._updatePovIndicator());
        this._panoA.addListener('position_changed', () => this._updatePovIndicator());
        this._panoB.addListener('position_changed', () => this._updatePovIndicator());

        // Classic Marker: AdvancedMarkerElement does not render inside panoramas.
        this.panoMarker = new google.maps.Marker({
            position: { lat: 0, lng: 0 },
            title: 'Device Location',
            icon: {
                url: 'css/images/security/atm_protector_marker.png',
                scaledSize: new google.maps.Size(36, 36),
                anchor: new google.maps.Point(18, 36)
            }
        });
    }

    _panoEls() {
        return {
            a: this.querySelector('.stm-pano-a'),
            b: this.querySelector('.stm-pano-b')
        };
    }

    _frontPano() {
        return this._panoFrontIsA ? this._panoA : this._panoB;
    }

    _backPano() {
        return this._panoFrontIsA ? this._panoB : this._panoA;
    }

    _afterSplitToggle() {
        this.updateComplete.then(() => {
            if (this.map) google.maps.event.trigger(this.map, 'resize');
            if (this._panoA) google.maps.event.trigger(this._panoA, 'resize');
            if (this._panoB) google.maps.event.trigger(this._panoB, 'resize');
        });
    }

    // Apply a panorama to the back buffer, then crossfade once imagery is ready.
    // Same pano id only updates heading on the front (no reload / no flash).
    // reqId ties this reveal to the latest getPanorama request.
    _showPanoSmooth(panoId, heading, target, kind, reqId) {
        if (!this._panoA || !this._panoB) return;
        if (reqId != null && reqId !== this._panoLoadSeq) return;
        const front = this._frontPano();
        this.panorama = front;

        if (panoId && panoId === this._lastPanoId && front.getVisible()) {
            this._lastPanoHeading = heading;
            front.setPov({ heading, pitch: 0 });
            this._setPanoMarker(target, kind);
            this.panoAvailable = true;
            return;
        }

        const back = this._backPano();
        const els = this._panoEls();
        const frontEl = this._panoFrontIsA ? els.a : els.b;
        const backEl = this._panoFrontIsA ? els.b : els.a;
        if (!frontEl || !backEl) return;

        const loadId = reqId != null ? reqId : this._panoLoadSeq;
        const hasPrior = !!(this._lastPanoId && front.getVisible());

        // First imagery: load into the front pane directly (nothing to crossfade from).
        if (!hasPrior) {
            front.setOptions({ visible: true });
            front.setPano(panoId);
            front.setPov({ heading, pitch: 0 });
            this._lastPanoId = panoId;
            this._lastPanoHeading = heading;
            this.panorama = front;
            if (this.map) this.map.setStreetView(front);
            this._setPanoMarker(target, kind);
            this.panoAvailable = true;
            return;
        }

        // Subsequent loads: paint into the hidden back buffer, then fade it in.
        back.setOptions({ visible: true });
        back.setPano(panoId);
        back.setPov({ heading, pitch: 0 });

        const reveal = () => {
            if (loadId !== this._panoLoadSeq) return;
            if (this._panoSwapTimer) {
                clearTimeout(this._panoSwapTimer);
                this._panoSwapTimer = null;
            }
            backEl.classList.add('stm-pano-front');
            backEl.classList.remove('stm-pano-back');
            frontEl.classList.add('stm-pano-back');
            frontEl.classList.remove('stm-pano-front');
            this._panoFrontIsA = !this._panoFrontIsA;
            this.panorama = this._frontPano();
            this._lastPanoId = panoId;
            this._lastPanoHeading = heading;
            if (this.map) this.map.setStreetView(this.panorama);
            this._setPanoMarker(target, kind);
            this.panoAvailable = true;
        };

        const onReady = () => {
            if (loadId !== this._panoLoadSeq) return;
            if (back.getStatus && back.getStatus() !== 'OK') {
                this.panoAvailable = false;
                return;
            }
            // Brief settle so tiles paint before the fade.
            this._panoSwapTimer = setTimeout(reveal, 80);
        };

        google.maps.event.addListenerOnce(back, 'status_changed', onReady);
        if (back.getStatus && back.getStatus() === 'OK' && back.getPano() === panoId) {
            onReady();
        }
    }

    // kind: 'gps' | 'wifi' (device pin, current location) or 'dot' (historical point)
    _panoIcon(kind) {
        if (kind === 'dot') {
            return {
                url: PANO_DOT_ICON,
                scaledSize: new google.maps.Size(22, 22),
                anchor: new google.maps.Point(11, 11)
            };
        }
        return {
            url: `css/images/security/${kind === 'wifi' ? 'atm_wifi_marker.png' : 'atm_protector_marker.png'}`,
            scaledSize: new google.maps.Size(36, 36),
            anchor: new google.maps.Point(18, 36)
        };
    }

    _setPanoMarker(target, kind = 'gps') {
        if (!this.panoMarker) return;
        if (kind !== this._panoMarkerKind) {
            this._panoMarkerKind = kind;
            this.panoMarker.setIcon(this._panoIcon(kind));
        }
        this.panoMarker.setPosition(target);
        this.panoMarker.setMap(this.panorama);
    }

    // Historical points render as the red playback dot; the newest point (the
    // device's current location) renders as the matching GPS/WiFi device pin.
    _trackPointKind(i) {
        if (i < this.points.length - 1) return 'dot';
        const p = this.points[i];
        return p && p.direction === 11 ? 'wifi' : 'gps';
    }

    // Camera-direction indicator: a view cone drawn on the main map at the
    // panorama position, rotated to match the Street View heading.
    _ensurePovMarker() {
        if (this.povMarker) return;
        const wrap = document.createElement('div');
        wrap.className = 'stm-pov-wrap';
        const rot = document.createElement('div');
        rot.className = 'stm-pov-rot';
        rot.innerHTML = '<div class="stm-pov-cone"></div>';
        wrap.appendChild(rot);
        // Camera icon stays upright; only the cone rotates with the heading.
        const cam = document.createElement('div');
        cam.className = 'stm-pov-cam';
        cam.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16">' +
            '<path fill="#fb8c00" d="M9 3 7.2 5H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.2L15 3H9zm3 14.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-1.8a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z"/>' +
            '</svg>';
        wrap.appendChild(cam);
        this._povRotEl = rot;
        this.povMarker = new google.maps.marker.AdvancedMarkerElement({
            position: { lat: 0, lng: 0 },
            zIndex: 9000, // beneath the device marker
            title: 'Street View Camera',
            content: wrap
        });
    }

    _updatePovIndicator() {
        if (!this.map || !this.panorama) return;
        if (!this.streetViewOn || !this.panorama.getVisible()) {
            this._hidePovIndicator();
            return;
        }
        const pos = this.panorama.getPosition();
        if (!pos) return;
        this._ensurePovMarker();
        this.povMarker.position = pos;
        this.povMarker.map = this.map;
        const pov = this.panorama.getPov();
        this._povRotEl.style.transform = `rotate(${(pov && pov.heading) || 0}deg)`;
    }

    _hidePovIndicator() {
        if (this.povMarker) this.povMarker.map = null;
    }

    // Static behavior: park the camera at the nearest panorama and aim it at the device.
    _pointStreetViewAt(target, kind = 'gps') {
        if (!this.streetViewOn || !this._panoA || !this.svService || !target) return;
        const reqId = ++this._panoLoadSeq;
        this.svService.getPanorama({ location: target, radius: 100, preference: 'nearest', source: 'outdoor' }, (data, svStatus) => {
            if (reqId !== this._panoLoadSeq) return;
            if (svStatus === 'OK' && data.location && data.location.latLng) {
                const panoLoc = { lat: data.location.latLng.lat(), lng: data.location.latLng.lng() };
                const heading = computeBearing(panoLoc, target);
                this._showPanoSmooth(data.location.pano, heading, target, kind, reqId);
            } else {
                // Keep the last good Street View; only show the unavailable overlay.
                this.panoAvailable = false;
            }
        });
    }

    // Playback behavior: camera follows the route, heading points in the direction of
    // travel; if the device is static at this point, fall back to looking at it.
    _updateStreetViewPlayback(i) {
        if (!this.streetViewOn || !this._panoA || this.points.length === 0) return;

        const idx = Math.min(i, this.points.length - 1);
        const kind = this._trackPointKind(idx);
        const cur = this.points[idx];
        const curPos = { lat: cur.latitude, lng: cur.longitude };

        // The marker is cheap to move: keep it glued to the playback position on
        // every update, even when the camera move below is rate-limited.
        const deviceNow = (this.playbackMarker && this.playbackMarker.position) || curPos;
        this._setPanoMarker(deviceNow, kind);

        // Panorama tile loads are heavy; rate-limit position changes.
        const now = Date.now();
        if (i === this._lastPanoIndex || now - this._lastPanoUpdate < 700) return;
        this._lastPanoIndex = i;
        this._lastPanoUpdate = now;

        const next = this.points[Math.min(i + 1, this.points.length - 1)];
        const nextPos = { lat: next.latitude, lng: next.longitude };
        const isMoving = Math.abs(nextPos.lat - curPos.lat) > 1e-6 || Math.abs(nextPos.lng - curPos.lng) > 1e-6;

        if (!isMoving) {
            this._pointStreetViewAt(curPos, kind);
            return;
        }

        const heading = computeBearing(curPos, nextPos);
        const reqId = ++this._panoLoadSeq;
        this.svService.getPanorama({ location: curPos, radius: 100, preference: 'nearest', source: 'outdoor' }, (data, svStatus) => {
            if (reqId !== this._panoLoadSeq) return;
            if (svStatus === 'OK' && data.location && data.location.pano) {
                this._showPanoSmooth(data.location.pano, heading, deviceNow, kind, reqId);
            } else {
                // Hold the previous panorama instead of blacking out the panel.
                this.panoAvailable = false;
            }
        });
    }

    _streetViewTarget() {
        if (this.mode !== 'live' && this.points.length > 0) {
            const i = Math.min(this.playbackIndex, this.points.length - 1);
            const p = this.points[i];
            return { pos: { lat: p.latitude, lng: p.longitude }, kind: this._trackPointKind(i) };
        }
        if (this.latlng) {
            const loc = this.getCurrentLocation(this.status);
            return {
                pos: { lat: this.latlng.lat(), lng: this.latlng.lng() },
                kind: loc && loc.direction === 11 ? 'wifi' : 'gps'
            };
        }
        return null;
    }

    /* ------------------------------------------------------------------ */
    /* Live marker                                                        */
    /* ------------------------------------------------------------------ */

    _hideLiveMarker() {
        if (this.marker) this.marker.map = null;
        // Close the live popup (and any leftover open IW) so History never shows
        // current-location details over the track.
        if (this.infoWindow && this._infoMode !== 'playback') {
            this.infoWindow.close();
            this._infoMode = null;
        }
    }

    _updateLiveMarker(loc, status) {
        let lat = loc.latitude;
        let lng = loc.longitude;
        if (DplLink.isDataMasked()) {
            lat = DplLink.rndLat();
            lng = DplLink.rndLon();
        }
        this.latlng = new google.maps.LatLng(lat, lng);
        this.marker.content = securityImg(loc.direction === 11 ? 'atm_wifi_marker.png' : 'atm_protector_marker.png', 36);
        this.marker.position = this.latlng;

        // History focuses on the playback path; keep the live pin cached but hidden
        // so it doesn't cover the track or show a "current location" popup.
        if (this.mode === 'historical') {
            this._hideLiveMarker();
            return;
        }

        this.marker.map = this.map;

        this.infoWindow.setContent(this._buildLiveInfoContent(loc, status));
        this._infoMode = 'live';

        if (this.firstView) {
            this.firstView = false;
            this.infoWindow.open(this.map, this.marker);
        }

        if (this.streetViewOn && this.mode === 'live') {
            this._pointStreetViewAt({ lat, lng }, loc.direction === 11 ? 'wifi' : 'gps');
        }

        if (!this.modifiedView && this.mode === 'live') {
            this.map.setCenter({ lat, lng });
        }

        this._geocodeLatLng();

        // Current tower in live mode when toggled on
        if (this.mode === 'live') {
            if (this.showTowers && status.cellstate) {
                this._showCurrentTower(status);
            } else if (!this.showTowers) {
                this._hideCurrentTower();
            }
        }
    }

    _buildLiveInfoContent(loc, status) {
        const isWifi = loc.direction === 11;
        const srcBadge = `<span class="stm-iw-src ${isWifi ? 'stm-iw-src-wifi' : 'stm-iw-src-gps'}">${isWifi ? 'WiFi' : 'GPS'}</span>`;
        const m = moment(DplLink.dateFromPostgress(loc.eventtime));
        const timeRow = `<div class="stm-iw-meta"><span>${m.format('h:mm A, MMM D')}</span><span class="stm-iw-ago">${m.fromNow()}</span></div>`;

        if (DplLink.isDataMasked()) {
            return `<div class="stm-iw">
                <div class="stm-iw-head"><span class="stm-iw-title">${DplLink.rndLocation()}</span>${srcBadge}</div>
                <div class="stm-iw-addr">${DplLink.rndStreet()}</div>
                ${timeRow}
            </div>`;
        }

        const name = status.mname || 'Device';
        const mid = status.mid ? `<span class="stm-iw-sub">${status.mid}</span>` : '';
        const coords = this.latlng ? `${this.latlng.lat().toFixed(5)}, ${this.latlng.lng().toFixed(5)}` : '';
        return `<div class="stm-iw">
            <div class="stm-iw-head"><span class="stm-iw-title">${name}${mid}</span>${srcBadge}</div>
            <div class="stm-iw-addr" id="stm-geolocation">${this.geolocationShort || 'Locating address&hellip;'}</div>
            ${timeRow}
            <div class="stm-iw-foot">
                <span class="stm-iw-coords">${coords}</span>
                <button class="stm-iw-copy" title="Copy location details"><span class="copyIcon"></span></button>
            </div>
        </div>`;
    }

    _copyLocationDetails() {
        const status = this.status;
        const loc = status && this.getCurrentLocation(status);
        if (!loc || DplLink.isDataMasked()) return;
        const m = moment(DplLink.dateFromPostgress(loc.eventtime));
        const lines = [
            (status.mname || 'Device') + (status.mid ? ` (${status.mid})` : ''),
        ];
        if (this.geolocationShort) lines.push(this.geolocationShort);
        lines.push(`${m.format('h:mm A, MMM D, YYYY')} (${m.fromNow()})`);
        lines.push(`${Number(loc.latitude).toFixed(5)}, ${Number(loc.longitude).toFixed(5)}`);
        lines.push(`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`);
        DplLink.copyToClipboard(lines.join('\n'));
        DplLink.NetworkMsgPopup.show('Location Copied', 'success', 2000);
    }

    _playbackPointAtPlayhead() {
        if (!this.points.length) return null;
        const i = Math.min(this.playbackIndex, this.points.length - 1);
        return this.points[i] || null;
    }

    _buildPlaybackInfoContent(point, lat, lng) {
        const isWifi = point.direction === 11;
        const srcBadge = `<span class="stm-iw-src ${isWifi ? 'stm-iw-src-wifi' : 'stm-iw-src-gps'}">${isWifi ? 'WiFi' : 'GPS'}</span>`;
        const m = moment(DplLink.dateFromPostgress(point.eventtime));
        const clock = this._formatDeviceTime(this._trackStartMs + this.playbackPos);
        const clockLabel = `${clock.time.replace(/:\d{2} /, ' ')} ${clock.date}`;
        const timeRow = `<div class="stm-iw-meta"><span>${clockLabel}</span><span class="stm-iw-ago">${m.fromNow()}</span></div>`;
        const imperial = !!(DplLink.currentUser && DplLink.currentUser.configoptions &&
            DplLink.currentUser.configoptions.USEIMPERIAL !== 'f');
        const speed = point.speed != null ? `${point.speed} ${imperial ? 'mph' : 'km/h'}` : '';
        const heading = this._getDirectionText(point.direction);
        const idx = `${this.playbackIndex + 1} / ${this.points.length}`;

        if (DplLink.isDataMasked()) {
            return `<div class="stm-iw">
                <div class="stm-iw-head"><span class="stm-iw-title">${DplLink.rndLocation()}</span>${srcBadge}</div>
                <div class="stm-iw-addr">${DplLink.rndStreet()}</div>
                ${timeRow}
            </div>`;
        }

        const status = this.status || {};
        const name = status.mname || 'Device';
        const mid = status.mid ? `<span class="stm-iw-sub">${status.mid}</span>` : '';
        const coords = `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
        const rows = [
            speed ? `<div class="stm-iw-row"><span>Speed</span><span>${speed}</span></div>` : '',
            heading && heading !== 'WiFi' ? `<div class="stm-iw-row"><span>Heading</span><span>${heading}</span></div>` : '',
            `<div class="stm-iw-row"><span>Point</span><span>${idx}</span></div>`
        ].filter(Boolean).join('');

        return `<div class="stm-iw">
            <div class="stm-iw-head"><span class="stm-iw-title">${name}${mid}</span>${srcBadge}</div>
            ${timeRow}
            <div class="stm-iw-rows">${rows}</div>
            <div class="stm-iw-foot">
                <span class="stm-iw-coords">${coords}</span>
                <button class="stm-iw-copy" title="Copy location details"><span class="copyIcon"></span></button>
            </div>
        </div>`;
    }

    _copyPlaybackDetails() {
        const point = this._playbackPointAtPlayhead();
        if (!point || DplLink.isDataMasked()) return;
        const status = this.status || {};
        const pos = this.playbackMarker && this.playbackMarker.position;
        const lat = pos ? (typeof pos.lat === 'function' ? pos.lat() : pos.lat) : point.latitude;
        const lng = pos ? (typeof pos.lng === 'function' ? pos.lng() : pos.lng) : point.longitude;
        const m = moment(DplLink.dateFromPostgress(point.eventtime));
        const lines = [
            (status.mname || 'Device') + (status.mid ? ` (${status.mid})` : ''),
            `${m.format('h:mm A, MMM D, YYYY')} (${m.fromNow()})`,
            `${point.direction === 11 ? 'WiFi' : 'GPS'}` +
                (point.speed != null ? ` · ${point.speed}` : ''),
            `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`,
            `https://www.google.com/maps?q=${lat},${lng}`
        ];
        DplLink.copyToClipboard(lines.join('\n'));
        DplLink.NetworkMsgPopup.show('Location Copied', 'success', 2000);
    }

    _showPlaybackInfo() {
        if (this.mode !== 'historical' || !this.playbackMarker || !this.infoWindow) return;
        const point = this._playbackPointAtPlayhead();
        if (!point) return;
        const pos = this.playbackMarker.position;
        if (!pos) return;
        const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
        const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
        if (this._playbackInfoCloseTimer) {
            clearTimeout(this._playbackInfoCloseTimer);
            this._playbackInfoCloseTimer = null;
        }
        this.infoWindow.setContent(this._buildPlaybackInfoContent(point, lat, lng));
        this._infoMode = 'playback';
        this.infoWindow.open({ map: this.map, anchor: this.playbackMarker, shouldFocus: false });
        google.maps.event.addListenerOnce(this.infoWindow, 'domready', () => {
            const iw = document.querySelector('.gm-style-iw-c');
            if (!iw) return;
            iw.addEventListener('mouseenter', () => {
                if (this._playbackInfoCloseTimer) {
                    clearTimeout(this._playbackInfoCloseTimer);
                    this._playbackInfoCloseTimer = null;
                }
            });
            iw.addEventListener('mouseleave', () => this._scheduleHidePlaybackInfo());
        });
    }

    _scheduleHidePlaybackInfo() {
        if (this._playbackInfoCloseTimer) clearTimeout(this._playbackInfoCloseTimer);
        this._playbackInfoCloseTimer = setTimeout(() => {
            this._playbackInfoCloseTimer = null;
            if (this._infoMode === 'playback' && this.infoWindow) {
                this.infoWindow.close();
                this._infoMode = null;
            }
        }, 200);
    }

    _hidePlaybackInfo() {
        if (this._playbackInfoCloseTimer) {
            clearTimeout(this._playbackInfoCloseTimer);
            this._playbackInfoCloseTimer = null;
        }
        if (this._infoMode === 'playback' && this.infoWindow) {
            this.infoWindow.close();
            this._infoMode = null;
        }
    }

    _geocodeLatLng() {
        if (!this.latlng || !this.geocoder) return;
        if (this.geopoints !== this.latlng.toString()) {
            this.geopoints = this.latlng.toString();
            this.geocoder.geocode({ location: this.latlng }, (results, geoStatus) => {
                if (geoStatus === 'OK' && results[0] && results[0].address_components.length >= 3) {
                    const ac = results[0].address_components;
                    this.geolocationShort = `${ac[0].long_name} ${ac[1].long_name}, ${ac[2].long_name}`;
                    const el = this.querySelector('#stm-geolocation');
                    if (el) el.innerHTML = this.geolocationShort;
                }
            });
        } else {
            const el = this.querySelector('#stm-geolocation');
            if (el) el.innerHTML = this.geolocationShort;
        }
    }

    /* ------------------------------------------------------------------ */
    /* Current cell tower (live / cellular fallback)                      */
    /* ------------------------------------------------------------------ */

    _showCurrentTower(status) {
        if (!status.cellstate) return;
        let radioType = 'lte';
        const ctypeint = parseInt(status.ctype, 10);
        if (ctypeint === 2 || ctypeint === 3) radioType = 'gsm';

        const cellArray = status.cellstate.split(',');
        if (cellArray.length < 4) return;

        const cellJson = {
            considerIp: 'false',
            radioType,
            cellTowers: [{
                cellId: parseInt(cellArray[3], 10),
                locationAreaCode: parseInt(cellArray[2], 10),
                mobileCountryCode: parseInt(cellArray[0], 10),
                mobileNetworkCode: parseInt(cellArray[1], 10)
            }]
        };

        const clocation = status.celllocation && status.celllocation.lat !== undefined ? status.celllocation : null;

        if (this.cellTowerData === null || JSON.stringify(this.cellTowerData) !== JSON.stringify(cellJson)) {
            this.cellTowerData = cellJson;
            if (clocation) {
                this._drawCurrentTower({ lat: clocation.lat, lng: clocation.lng }, clocation.accuracy);
            } else {
                fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${DplLink.protectorGoogleAPIKey}`, {
                    method: 'POST',
                    body: JSON.stringify(cellJson),
                    headers: { 'Content-Type': 'application/json' }
                })
                .then(response => response.json())
                .then(data => {
                    if (DplLink.isDataMasked()) {
                        data.location.lat = DplLink.rndLat();
                        data.location.lng = DplLink.rndLon();
                    }
                    this._drawCurrentTower(data.location, data.accuracy);
                })
                .catch(error => console.error('Error fetching cell location:', error));
            }
        } else if (this.cellCircle && this.cellMarker) {
            this.cellCircle.setVisible(true);
            this.cellMarker.map = this.map;
            if (this.cellOnly) {
                this.map.setCenter(this.cellLocation);
            }
        }
    }

    _drawCurrentTower(location, accuracy) {
        this.cellLocation = location;
        if (!this._deviceTimeZoneId) {
            this._fetchDeviceTimezone(location.lat, location.lng);
        }
        if (!this.cellCircle) {
            this.cellCircle = new google.maps.Circle({
                strokeColor: '#f7ce46',
                strokeOpacity: 0.2,
                strokeWeight: 1,
                fillColor: '#f7ce46',
                fillOpacity: 0.1,
                map: this.map,
                center: location,
                radius: accuracy
            });
        } else {
            this.cellCircle.setRadius(accuracy);
            this.cellCircle.setCenter(location);
            this.cellCircle.setVisible(true);
        }

        if (!this.cellMarker) {
            this.cellMarker = new google.maps.marker.AdvancedMarkerElement({
                position: location,
                title: 'Connected Cell Tower',
                content: centeredImg('cell_tower.png', 48),
                map: this.map
            });
        } else {
            this.cellMarker.position = location;
            this.cellMarker.map = this.map;
        }

        if (this.cellOnly) {
            this.map.setCenter(location);
            if (this.cellCircle) {
                this.map.fitBounds(this.cellCircle.getBounds());
            }
        }
    }

    _hideCurrentTower() {
        if (this.cellCircle) this.cellCircle.setVisible(false);
        if (this.cellMarker) this.cellMarker.map = null;
    }

    /* ------------------------------------------------------------------ */
    /* Track history (alert + historical)                                 */
    /* ------------------------------------------------------------------ */

    _exitTrackMode() {
        this.mode = 'live';
        this.historicalRange = null;
        this._clearTrack();
        this._hideCurrentTower();
        this.modifiedView = false;
        if (this.map) {
            this.map.setZoom(18); // undo fitBounds zoom from track view
        }
        const status = this.status;
        if (status) {
            const loc = this.getCurrentLocation(status);
            if (this.isLocationFresh(loc, status)) {
                this._updateLiveMarker(loc, status);
            }
        }
    }

    // Token-authorized POST used by the standalone shared-link page, where the
    // portal passthrough endpoints (which need a session) are unavailable.
    _externalPost(path, body, success, error) {
        fetch(`${DplLink.resthost}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token: this.exttoken,
                adminid: parseInt(this.extadminid, 10),
                machineid: parseInt(this.device, 10),
                ...body
            })
        })
            .then(r => r.json())
            .then(data => {
                if (data && data.error) throw new Error(data.desc || 'Link error');
                success(data);
            })
            .catch(err => error(err));
    }

    _loadTrack(start, end) {
        this.loadingTrack = true;
        this.trackError = null;
        const handlers = {
            success: (response) => {
                this.loadingTrack = false;
                this.trackData = response;
                const points = Array.isArray(response.points) ? response.points : [];
                this.rawPoints = [...points].reverse(); // SQL returns newest first
                this.truncatedTrack = points.length >= 1000;
                const cleaned = buildCleanedTrack(this.rawPoints, response.motions || []);
                this.cleanedPoints = cleaned.points;
                this.stopClusters = cleaned.stops;
                this.cleanStats = cleaned.stats;
                this._hideLiveMarker();
                this._applyPathMode(true);
                if (this.showTowers) {
                    this._drawTowers();
                }
                // Aim street view at the device's most recent position.
                if (this.streetViewOn && this.rawPoints.length > 0) {
                    const newest = this.rawPoints[this.rawPoints.length - 1];
                    this._pointStreetViewAt(
                        { lat: newest.latitude, lng: newest.longitude },
                        newest.direction === 11 ? 'wifi' : 'gps'
                    );
                }
            },
            error: (jqXHR, textStatus, errorThrown) => {
                this.loadingTrack = false;
                this.trackError = 'Problem loading track history';
                DplLink.NetworkMsgPopup.show('Problem Loading Track History', 'error', 2000);
                console.log(textStatus, errorThrown, jqXHR && jqXHR.status, jqXHR && jqXHR.responseText);
            }
        };
        if (this.external) {
            this._externalPost('/r/externalmaptrack',
                { start: start.toISOString(), end: end.toISOString() },
                handlers.success, handlers.error);
        } else {
            DplLink.apiCall({
                url: DplLink.resthost + DplLink.apiUrls.securityTrackHistory,
                data: {
                    p1: parseInt(this.device, 10),
                    p2: start.toISOString(),
                    p3: end.toISOString()
                },
                success: handlers.success,
                error: handlers.error
            });
        }
    }

    // Switch the active playback/draw set between cleaned and raw points.
    _applyPathMode(fitBounds) {
        const next = this.pathMode === 'raw' ? this.rawPoints : this.cleanedPoints;
        this.points = next && next.length ? next : this.rawPoints;
        this._rebuildTimeline();
        if (this.points.length > 0) {
            const newest = this.points[this.points.length - 1];
            this._fetchDeviceTimezone(newest.latitude, newest.longitude);
        }
        this._resetPlayback();
        this._drawTrack(!!fitBounds);
        if (this.points.length > 0) {
            this._ensurePlaybackMarker();
            this._updatePlaybackMarker();
        }
        this.requestUpdate();
    }

    onTogglePathMode() {
        this.pathMode = this.pathMode === 'cleaned' ? 'raw' : 'cleaned';
        this._applyPathMode(true);
    }

    // Export the currently visible History range (trimmed/zoomed window +
    // cleaned/raw mode) as CSV for offline review.
    onExportCsv() {
        const points = this._windowPoints();
        if (!points.length) {
            DplLink.NetworkMsgPopup.show('No track points to export', 'error', 2000);
            return;
        }

        const imperial = !!(DplLink.currentUser && DplLink.currentUser.configoptions &&
            DplLink.currentUser.configoptions.USEIMPERIAL !== 'f');
        const speedUnit = imperial ? 'mph' : 'km/h';
        const rows = [[
            'eventtime_utc',
            'eventtime_device_local',
            'latitude',
            'longitude',
            'speed',
            'speed_unit',
            'source',
            'heading',
            'locationlogid',
            'is_stop',
            'path_mode',
            'maps_url'
        ]];

        points.forEach((p) => {
            const d = DplLink.dateFromPostgress(p.eventtime);
            const ms = d ? d.getTime() : 0;
            const utc = d ? d.toISOString() : String(p.eventtime || '');
            let local = '';
            if (ms) {
                const clock = this._formatDeviceTime(ms);
                local = `${clock.time} ${clock.date}${clock.tz ? ` ${clock.tz}` : ''}`;
            }
            const lat = Number(p.latitude);
            const lng = Number(p.longitude);
            const isWifi = Number(p.direction) === 11;
            rows.push([
                utc,
                local,
                Number.isFinite(lat) ? lat.toFixed(6) : '',
                Number.isFinite(lng) ? lng.toFixed(6) : '',
                p.speed != null ? p.speed : '',
                speedUnit,
                p._stop ? 'Stop' : (isWifi ? 'WiFi' : 'GPS'),
                p._stop ? '' : this._getDirectionText(Number(p.direction)),
                p.locationlogid != null ? p.locationlogid : '',
                p._stop ? 1 : 0,
                this.pathMode,
                Number.isFinite(lat) && Number.isFinite(lng)
                    ? `https://www.google.com/maps?q=${lat},${lng}`
                    : ''
            ]);
        });

        const mid = (this.status && this.status.mid) || this.device || 'device';
        const w = this._timelineWindow();
        const startMs = this._trackStartMs + w.start;
        const endMs = this._trackStartMs + w.end;
        const stamp = (ms) => moment(ms).utc().format('YYYYMMDDTHHmmss');
        const filename = `security-track-${mid}-${stamp(startMs)}-${stamp(endMs)}-${this.pathMode}.csv`;

        if (typeof DplLink.exportToCsv === 'function') {
            DplLink.exportToCsv(filename, rows, true);
        } else {
            this._downloadCsv(filename, rows);
        }
        if (DplLink.NetworkMsgPopup && DplLink.NetworkMsgPopup.show) {
            DplLink.NetworkMsgPopup.show(`Exported ${points.length} points`, 'success', 2000);
        }
    }

    _downloadCsv(filename, rows) {
        const escape = (val) => {
            const s = (val === null || val === undefined) ? '' : String(val);
            return /[",\n\t]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = rows.map(row => row.map(escape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // Points inside the focused (trimmed/zoomed) timeline window; falls back to
    // the full track when no window is set.
    _windowPoints() {
        if (this._winStartMs === null || this._pointTimes.length !== this.points.length) {
            return this.points;
        }
        const s = this._trackStartMs + this._winStartMs;
        const e = this._trackStartMs + this._winEndMs;
        const pts = [];
        for (let i = 0; i < this.points.length; i++) {
            if (this._pointTimes[i] >= s && this._pointTimes[i] <= e) pts.push(this.points[i]);
        }
        return pts.length >= 2 ? pts : this.points;
    }

    _drawTrack(fitBounds) {
        this._clearPathOverlays();
        const points = this._windowPoints();
        if (points.length === 0) return;

        // Build polyline segments colored by source (GPS vs WiFi)
        let segment = [];
        let segmentIsWifi = null;
        const flush = () => {
            if (segment.length >= 2) {
                const line = new google.maps.Polyline({
                    path: segment,
                    geodesic: true,
                    strokeColor: segmentIsWifi ? WIFI_PATH_COLOR : GPS_PATH_COLOR,
                    strokeOpacity: 0.75,
                    strokeWeight: 4,
                    map: this.map
                });
                this.pathPolylines.push(line);
            }
        };

        points.forEach((p) => {
            const isWifi = p.direction === 11;
            const pos = { lat: p.latitude, lng: p.longitude };
            if (segmentIsWifi !== null && isWifi !== segmentIsWifi) {
                flush();
                segment = [segment[segment.length - 1]];
            }
            segmentIsWifi = isWifi;
            segment.push(pos);
        });
        flush();

        // Per-point markers, skipped on very dense tracks. In cleaned mode, skip
        // stop bookends (same coordinate twice) so the stop circle is the cue.
        if (points.length <= MAX_POINT_MARKERS) {
            points.forEach((p, idx) => {
                if (p._stop && idx > 0 && points[idx - 1]._stop &&
                    points[idx - 1].latitude === p.latitude &&
                    points[idx - 1].longitude === p.longitude) {
                    return;
                }
                const pointtype = p._stop
                    ? (p._wifiAnchored ? 'Stop (WiFi anchored)' : 'Stop')
                    : (p.direction === 11 ? 'WiFi Point' : 'GPS Point');
                const timeStr = moment(DplLink.dateFromPostgress(p.eventtime)).format('h:mm A, MMM D');
                const m = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat: p.latitude, lng: p.longitude },
                    title: `${pointtype} - ${timeStr}`,
                    content: centeredImg('gps_point.png', 18),
                    map: this.map
                });
                this.pointMarkers.push(m);
            });
        }

        if (this.pathMode === 'cleaned') {
            this._drawStopClusters();
        }

        if (fitBounds && points.length > 1) {
            const bounds = new google.maps.LatLngBounds();
            points.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
            this.map.fitBounds(bounds);
        }
    }

    _drawStopClusters() {
        const clusters = this.stopClusters || [];
        if (!clusters.length || !this.map) return;
        const w = this._timelineWindow();
        const winStart = this._trackStartMs + w.start;
        const winEnd = this._trackStartMs + w.end;
        clusters.forEach((s) => {
            // Hide stops outside the focused timeline window when zoomed/trimmed.
            if (this._winStartMs !== null && (s.endMs < winStart || s.startMs > winEnd)) return;
            const circle = new google.maps.Circle({
                strokeColor: '#5c6b7a',
                strokeOpacity: 0.45,
                strokeWeight: 1,
                fillColor: '#5c6b7a',
                fillOpacity: 0.12,
                map: this.map,
                center: { lat: s.latitude, lng: s.longitude },
                radius: Math.min(Math.max(s.radius, 15), 80),
                clickable: false,
                zIndex: 1
            });
            this.stopOverlays.push(circle);
        });
    }

    _clearPathOverlays() {
        this.pathPolylines.forEach(line => line.setMap(null));
        this.pathPolylines = [];
        this.pointMarkers.forEach(m => m.map = null);
        this.pointMarkers = [];
        this.stopOverlays.forEach(o => o.setMap(null));
        this.stopOverlays = [];
    }

    _clearTrack() {
        this._stopPlaybackLoop();
        this.isPlaying = false;
        this.points = [];
        this.rawPoints = [];
        this.cleanedPoints = [];
        this.stopClusters = [];
        this.cleanStats = null;
        this.trackData = null;
        this.truncatedTrack = false;
        this._clearPathOverlays();
        this._clearTowers();
        this._hidePlaybackInfo();
        if (this.playbackMarker) {
            this.playbackMarker.map = null;
        }
    }

    /* ------------------------------------------------------------------ */
    /* Cell tower overlay (track modes)                                   */
    /* ------------------------------------------------------------------ */

    _drawTowers() {
        this._clearTowers();
        const towers = (this.trackData && this.trackData.towers) || [];
        towers.forEach((t) => {
            if (t.latitude === null || t.latitude === undefined) return;
            const pos = { lat: t.latitude, lng: t.longitude };

            const circle = new google.maps.Circle({
                strokeColor: '#f7ce46',
                strokeOpacity: 0.25,
                strokeWeight: 1,
                fillColor: '#f7ce46',
                fillOpacity: 0.08,
                map: this.map,
                center: pos,
                radius: t.accuracy || 500
            });

            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: pos,
                title: `Cell Tower ${t.carrier || ''}`,
                content: centeredImg('cell_tower.png', 36),
                map: this.map
            });

            marker.addListener('click', () => {
                const first = moment(DplLink.dateFromPostgress(t.firstseen)).format('h:mm A, MMM D');
                const last = moment(DplLink.dateFromPostgress(t.lastseen)).format('h:mm A, MMM D');
                this.infoWindow.setContent(
                    `<div class="stm-iw">
                        <div class="stm-iw-head">
                            <span class="stm-iw-title">Cell Tower</span>
                            <span class="stm-iw-src stm-iw-src-cell">${t.radiotech || 'Cell'}</span>
                        </div>
                        <div class="stm-iw-rows">
                            <div class="stm-iw-row"><span>Carrier</span><span>${t.carrier || 'Unknown'}</span></div>
                            <div class="stm-iw-row"><span>Cell ID</span><span>${t.cloc}</span></div>
                            ${t.simused ? `<div class="stm-iw-row"><span>SIM</span><span>${t.simused}</span></div>` : ''}
                            <div class="stm-iw-row"><span>In use</span><span>${first} &rarr; ${last}</span></div>
                        </div>
                    </div>`
                );
                this.infoWindow.open(this.map, marker);
            });

            this.towerOverlays.push(circle, marker);
        });
    }

    _clearTowers() {
        this.towerOverlays.forEach((o) => {
            if (o.setMap) o.setMap(null);
            else o.map = null;
        });
        this.towerOverlays = [];
    }

    /* ------------------------------------------------------------------ */
    /* Playback                                                           */
    /* ------------------------------------------------------------------ */

    // Epoch-ms timestamps for each point; playback advances along this timeline
    // so marker motion is proportional to the real reporting gaps.
    _rebuildTimeline() {
        this._pointTimes = this.points.map(p => {
            const d = DplLink.dateFromPostgress(p.eventtime);
            return d ? d.getTime() : 0;
        });
        this._trackStartMs = this._pointTimes[0] || 0;
        const end = this._pointTimes[this._pointTimes.length - 1] || 0;
        this._trackDurationMs = Math.max(end - this._trackStartMs, 0);
    }

    // Device-local timezone from a known position (Google Time Zone API).
    // Works from GPS/WiFi fixes and, for cellular-only views, the tower location.
    _fetchDeviceTimezone(lat, lng) {
        if (lat === undefined || lng === undefined) return;
        const ts = Math.floor(Date.now() / 1000);
        fetch(`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${ts}&key=${DplLink.protectorGoogleAPIKey}`)
            .then(r => r.json())
            .then(data => {
                if (data && data.status === 'OK' && data.timeZoneId) {
                    this._deviceTimeZoneId = data.timeZoneId;
                    this._deviceTimeFmt = new Intl.DateTimeFormat('en-US', {
                        timeZone: data.timeZoneId,
                        hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
                        month: 'short', day: 'numeric', timeZoneName: 'short'
                    });
                    this.requestUpdate();
                }
            })
            .catch(() => { /* fall back to browser-local time */ });
    }

    _formatDeviceTime(ms) {
        if (this._deviceTimeFmt) {
            try {
                const parts = this._deviceTimeFmt.formatToParts(new Date(ms));
                const get = (type) => {
                    const part = parts.find(pt => pt.type === type);
                    return part ? part.value : '';
                };
                return {
                    time: `${get('hour')}:${get('minute')}:${get('second')} ${get('dayPeriod')}`,
                    date: `${get('month')} ${get('day')}`,
                    tz: get('timeZoneName')
                };
            } catch (e) { /* fall through to browser-local */ }
        }
        const m = moment(ms);
        return { time: m.format('h:mm:ss A'), date: m.format('MMM D'), tz: '' };
    }

    // Selected history range expressed in the device's local timezone; null when
    // the timezone is unknown or matches the browser's (note would be redundant).
    _deviceRangeNote() {
        if (!this._deviceTimeZoneId || !this.historicalRange) return null;
        try {
            if (Intl.DateTimeFormat().resolvedOptions().timeZone === this._deviceTimeZoneId) return null;
            const fmt = new Intl.DateTimeFormat('en-US', {
                timeZone: this._deviceTimeZoneId,
                month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit', hour12: true,
                timeZoneName: 'short'
            });
            return `${fmt.format(this.historicalRange.start.toDate())} - ${fmt.format(this.historicalRange.end.toDate())}`;
        } catch (e) {
            return null;
        }
    }

    _resetPlayback() {
        this._stopPlaybackLoop();
        this.isPlaying = false;
        this.playbackPos = 0;
        this.playbackIndex = 0;
        this._userScrubbed = false;
        this._winStartMs = null;
        this._winEndMs = null;
        this._trim = null;
        if (this.playbackMarker) {
            this.playbackMarker.map = null;
        }
    }

    _ensurePlaybackMarker() {
        if (!this.playbackMarker) {
            const dot = document.createElement('div');
            dot.className = 'stm-playback-dot';
            this.playbackMarker = new google.maps.marker.AdvancedMarkerElement({
                position: { lat: 0, lng: 0 },
                zIndex: 20000,
                title: 'Playback Position',
                content: dot
            });
            // Hover shows details for the timeline position under the playhead.
            dot.addEventListener('mouseenter', () => this._showPlaybackInfo());
            dot.addEventListener('mouseleave', () => this._scheduleHidePlaybackInfo());
            // Click keeps the popup available for copy / touch.
            this.playbackMarker.addListener('gmp-click', () => this._showPlaybackInfo());
            this.playbackMarker.addListener('click', () => this._showPlaybackInfo());
        }
        this.playbackMarker.map = this.map;
    }

    onTogglePlay() {
        if (this.isPlaying) {
            this.isPlaying = false;
            this._stopPlaybackLoop();
        } else {
            if (this.points.length < 2 || this._trackDurationMs <= 0) return;
            // Play within the focused (trimmed/zoomed) window.
            const w = this._timelineWindow();
            if (this.playbackPos >= w.end || this.playbackPos < w.start) {
                this.playbackPos = w.start;
            }
            this._ensurePlaybackMarker();
            this.isPlaying = true;
            this._userScrubbed = true;
            this._lastFrameTs = null;
            this._rafId = requestAnimationFrame(this._playbackStep);
        }
    }

    onCycleSpeed() {
        // 0.5x -> 1x -> 2x -> 0.5x
        this.playbackSpeed = this.playbackSpeed === 0.5 ? 1 : (this.playbackSpeed === 1 ? 2 : 0.5);
    }

    /* Timeline zoom: narrow the scrubber to a sub-window of the track so exact
       moments are easy to dial in. Wheel-zoom centers on the cursor. */

    _timelineWindow() {
        return {
            start: this._winStartMs === null ? 0 : this._winStartMs,
            end: this._winEndMs === null ? Math.max(this._trackDurationMs, 1) : this._winEndMs
        };
    }

    _minWindowWidth() {
        return Math.max(Math.min(this._trackDurationMs, 30000), this._trackDurationMs / 500);
    }

    // Single entry point for changing the focus window (zoom and trim handles).
    _setWindow(start, end) {
        const minWidth = this._minWindowWidth();
        let s = Math.max(0, start);
        let e = Math.min(this._trackDurationMs, end);
        if (e - s < minWidth) {
            const c = (s + e) / 2;
            s = Math.max(0, c - minWidth / 2);
            e = Math.min(this._trackDurationMs, s + minWidth);
            s = Math.max(0, e - minWidth);
        }
        if (s <= 0 && e >= this._trackDurationMs) {
            this._winStartMs = null;
            this._winEndMs = null;
        } else {
            this._winStartMs = s;
            this._winEndMs = e;
            // Keep the playhead inside the window so the scrubber stays meaningful.
            const clamped = Math.max(s, Math.min(this.playbackPos, e));
            if (clamped !== this.playbackPos) {
                this.playbackPos = clamped;
                if (this.playbackMarker && this.playbackMarker.map) this._updatePlaybackMarker();
            }
        }
        this._scheduleWindowRedraw();
        this.requestUpdate();
    }

    // Redraw the map track to match the focused window. Debounced so wheel
    // zooming doesn't refit the map on every tick.
    _scheduleWindowRedraw() {
        if (this._winRedrawTimer) clearTimeout(this._winRedrawTimer);
        this._winRedrawTimer = setTimeout(() => {
            this._winRedrawTimer = null;
            if (this.mode === 'live' || this.points.length === 0 || !this.map) return;
            this._drawTrack(true);
        }, 200);
    }

    onTimelineZoom(factor, centerMs) {
        if (this._trackDurationMs <= 0) return;
        const w = this._timelineWindow();
        const width = w.end - w.start;
        const newWidth = Math.max(Math.min(width / factor, this._trackDurationMs), this._minWindowWidth());

        const c = centerMs !== undefined ? centerMs
            : (this.playbackPos >= w.start && this.playbackPos <= w.end ? this.playbackPos : (w.start + w.end) / 2);
        const frac = width > 0 ? (c - w.start) / width : 0.5;
        let s = c - newWidth * frac;
        s = Math.max(0, Math.min(s, this._trackDurationMs - newWidth));
        this._setWindow(s, s + newWidth);
    }

    onTimelineWheel(e) {
        if (this._trackDurationMs <= 0) return;
        e.preventDefault();
        const w = this._timelineWindow();
        const rect = e.currentTarget.getBoundingClientRect();
        const frac = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        const centerMs = w.start + frac * (w.end - w.start);
        this.onTimelineZoom(e.deltaY < 0 ? 1.5 : 1 / 1.5, centerMs);
    }

    onZoomReset() {
        this._winStartMs = null;
        this._winEndMs = null;
        this._scheduleWindowRedraw();
        this.requestUpdate();
    }

    _winLabel(msOffset) {
        const t = this._formatDeviceTime(this._trackStartMs + msOffset);
        // h:mm:ss AM -> h:mm AM; the full time lives in the clock pill
        return `${t.time.replace(/:\d{2} /, ' ')} ${t.date}`;
    }

    // Subtle tick per point on the timeline strip so it's obvious where to zoom in.
    _drawTimelineCanvas() {
        const tl = this.querySelector('.stm-tl');
        const canvas = this.querySelector('.stm-tl-canvas');
        if (!tl || !canvas || this.points.length === 0) return;
        const rect = tl.getBoundingClientRect();
        if (rect.width < 10) return;

        const w = this._timelineWindow();
        const dark = document.documentElement.getAttribute('data-theme') === 'dark';
        const sig = `${this.points.length}|${w.start.toFixed(0)}|${w.end.toFixed(0)}|${Math.round(rect.width)}|${dark ? 'd' : 'l'}`;
        if (sig === this._tlSig) return; // unchanged; skip the redraw
        this._tlSig = sig;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, rect.width, rect.height);

        ctx.strokeStyle = dark ? 'rgba(255,255,255,.14)' : 'rgba(0,0,0,.12)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, rect.height / 2);
        ctx.lineTo(rect.width, rect.height / 2);
        ctx.stroke();

        const width = Math.max(w.end - w.start, 1);
        for (let i = 0; i < this._pointTimes.length; i++) {
            const t = this._pointTimes[i] - this._trackStartMs;
            if (t < w.start || t > w.end) continue;
            const x = ((t - w.start) / width) * rect.width;
            ctx.strokeStyle = this.points[i].direction === 11
                ? (dark ? 'rgba(186,120,240,.75)' : 'rgba(157,78,221,.6)')
                : (dark ? 'rgba(100,170,255,.7)' : 'rgba(44,123,229,.5)');
            ctx.beginPath();
            ctx.moveTo(x, 5);
            ctx.lineTo(x, rect.height - 5);
            ctx.stroke();
        }
    }

    /* iOS-style trim handles: drag the edge handles inward to focus the window
       on a sub-range; playback then runs only within that range. */

    onTrimDown(e, side) {
        if (this._trackDurationMs <= 0) return;
        e.preventDefault();
        e.stopPropagation(); // don't start a scrub underneath
        e.currentTarget.setPointerCapture(e.pointerId);
        this._trim = { side, frac: side === 'l' ? 0 : 1 };
        this.requestUpdate();
    }

    onTrimMove(e) {
        if (!this._trim) return;
        const tl = this.querySelector('.stm-tl');
        if (!tl) return;
        const rect = tl.getBoundingClientRect();
        let frac = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        // Keep the handles from crossing each other.
        frac = this._trim.side === 'l' ? Math.min(frac, 0.95) : Math.max(frac, 0.05);
        this._trim.frac = frac;
        this.requestUpdate();
    }

    onTrimUp() {
        if (!this._trim) return;
        const t = this._trim;
        this._trim = null;
        const w = this._timelineWindow();
        const width = w.end - w.start;
        // Ignore sub-0.5% drags (accidental clicks on the handle).
        if (t.side === 'l' && t.frac > 0.005) {
            this._setWindow(w.start + t.frac * width, w.end);
        } else if (t.side === 'r' && t.frac < 0.995) {
            this._setWindow(w.start, w.start + t.frac * width);
        } else {
            this.requestUpdate();
        }
    }

    /* Drag-to-scrub on the custom timeline strip */

    _playheadFrac() {
        const w = this._timelineWindow();
        const width = Math.max(w.end - w.start, 1);
        return Math.min(Math.max((this.playbackPos - w.start) / width, 0), 1);
    }

    onTlPointerDown(e) {
        if (this._trackDurationMs <= 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        this._tlDragging = true;
        this._scrubFromEvent(e);
    }

    onTlPointerMove(e) {
        if (this._tlDragging) this._scrubFromEvent(e);
    }

    onTlPointerUp() {
        this._tlDragging = false;
    }

    _scrubFromEvent(e) {
        const rect = e.currentTarget.getBoundingClientRect();
        const fr = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        const w = this._timelineWindow();
        this._scrubToMs(w.start + fr * (w.end - w.start));
    }

    _scrubToMs(ms) {
        this.isPlaying = false;
        this._stopPlaybackLoop();
        this._userScrubbed = true;
        this.playbackPos = ms;
        this._ensurePlaybackMarker();
        this._lastPanoUpdate = 0; // make sure the final scrub position reaches street view
        this._lastPanoIndex = -1;
        this._updatePlaybackMarker();
    }

    _playbackStep(ts) {
        if (!this.isPlaying || !this.map) return;
        if (this._lastFrameTs === null) this._lastFrameTs = ts;
        const dt = ts - this._lastFrameTs;
        this._lastFrameTs = ts;

        // Compressed real-time: the focused window plays in PLAYBACK_BASE_SECONDS
        // at 1x, so gaps between points stay proportional to the reported times
        // whether you're viewing the full track or a trimmed range.
        const w = this._timelineWindow();
        const wEnd = w.end;
        const windowMs = Math.max(w.end - w.start, 1);
        const trackMsPerSecond = (windowMs / PLAYBACK_BASE_SECONDS) * this.playbackSpeed;
        this.playbackPos = Math.min(this.playbackPos + (dt / 1000) * trackMsPerSecond, wEnd);
        this._updatePlaybackMarker();

        if (this.playbackPos >= wEnd) {
            this.isPlaying = false;
        } else {
            this._rafId = requestAnimationFrame(this._playbackStep);
        }
    }

    _updatePlaybackMarker() {
        if (this.points.length === 0 || !this.playbackMarker) return;
        const times = this._pointTimes;
        const t = this._trackStartMs + this.playbackPos;

        // Find the segment containing playback time t (rescan from 0 on backwards scrub).
        let i = Math.min(this.playbackIndex, times.length - 1);
        if (times[i] > t) i = 0;
        while (i < times.length - 1 && times[i + 1] <= t) i++;

        const a = this.points[i];
        const b = this.points[Math.min(i + 1, this.points.length - 1)];
        const span = times[Math.min(i + 1, times.length - 1)] - times[i];
        const frac = span > 0 ? Math.min((t - times[i]) / span, 1) : 0;
        const lat = a.latitude + (b.latitude - a.latitude) * frac;
        const lng = a.longitude + (b.longitude - a.longitude) * frac;
        this.playbackMarker.position = { lat, lng };
        this.playbackIndex = i;
        this._updateStreetViewPlayback(i);
        // Keep an open playback popup in sync as the playhead moves.
        if (this._infoMode === 'playback' && this.infoWindow && this.infoWindow.getMap()) {
            this.infoWindow.setContent(this._buildPlaybackInfoContent(a, lat, lng));
        }
    }

    _stopPlaybackLoop() {
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    _getDirectionText(dirNum) {
        switch (dirNum) {
            case 1: return 'N';
            case 2: return 'NE';
            case 3: return 'E';
            case 4: return 'SE';
            case 5: return 'S';
            case 6: return 'SW';
            case 7: return 'W';
            case 8: return 'NW';
            case 11: return 'WiFi';
            default: return '';
        }
    }

    /* ------------------------------------------------------------------ */
    /* Toolbar actions                                                    */
    /* ------------------------------------------------------------------ */

    onCenter() {
        this.modifiedView = false;
        if (this.cellOnly && this.cellLocation) {
            this.map.setCenter(this.cellLocation);
        } else if (this.latlng) {
            this.map.setCenter(this.latlng);
        }
    }

    _hideStreetViewPanos() {
        this._panoLoadSeq += 1; // cancel in-flight reveals
        if (this._panoSwapTimer) {
            clearTimeout(this._panoSwapTimer);
            this._panoSwapTimer = null;
        }
        if (this._panoA) this._panoA.setVisible(false);
        if (this._panoB) this._panoB.setVisible(false);
        this._lastPanoId = null;
    }

    onStreetView() {
        if (this.cellOnly) return; // street view needs a GPS/WiFi fix
        this.streetViewOn = !this.streetViewOn;
        this._afterSplitToggle();
        if (this.streetViewOn) {
            this._lastPanoIndex = -1;
            const target = this._streetViewTarget();
            if (target) {
                this._pointStreetViewAt(target.pos, target.kind);
            }
        } else {
            this._hideStreetViewPanos();
            this._hidePovIndicator();
        }
    }

    onToggleTowers() {
        this.showTowers = !this.showTowers;
        if (this.showTowers) {
            if (this.mode === 'live') {
                const status = this.status;
                if (status) this._showCurrentTower(status);
            } else {
                this._drawTowers();
            }
        } else {
            this._hideCurrentTower();
            this._clearTowers();
        }
    }

    onBackToLive() {
        if (this.mode !== 'live') {
            this._exitTrackMode();
        }
    }

    // Calentim opens the picker from the same button; meanwhile, default the
    // historical view to the most recent security incident's timeline.
    onHistoryClick() {
        if (this.mode === 'live' && !this.historicalRange && this.incidents.length) {
            const r = this._incidentRange(this.incidents[0]);
            this._onRangeSelected(r.start, r.end);
        }
    }

    // Recent security incidents become history presets and the default range.
    _loadIncidents() {
        const handlers = {
            success: (response) => {
                const list = Array.isArray(response) ? response : [];
                this.incidents = list.map(i => {
                    const start = moment(DplLink.dateFromPostgress(i.starttime));
                    // endtime is fully resolved server-side: close time, next same-type
                    // incident (bookend), or last point - capped at 2 weeks after start.
                    const end = i.endtime
                        ? moment.max(start, moment.min(moment(DplLink.dateFromPostgress(i.endtime)), moment()))
                        : moment.min(start.clone().add(14, 'days'), moment());
                    return { ...i, start, end };
                });
                if (this.incidents.length && this.calendarSelect) {
                    // Rebuild the picker so the incident presets appear.
                    this.calendarSelect.destroy();
                    this.calendarSelect = null;
                    this._initCalendar();
                }
            },
            error: () => { /* incident presets are a nice-to-have */ }
        };
        if (this.external) {
            this._externalPost('/r/externalmapincidents', { maxcount: 3 }, handlers.success, handlers.error);
        } else {
            DplLink.apiCall({
                url: DplLink.resthost + DplLink.apiUrls.securityIncidents,
                data: { p1: parseInt(this.device, 10), p2: 3 },
                success: handlers.success,
                error: handlers.error
            });
        }
    }

    // Pad the incident window so the approach and aftermath are visible too.
    _incidentRange(incident) {
        return {
            start: incident.start.clone().subtract(1, 'hour'),
            end: moment.min(incident.end.clone().add(1, 'hour'), moment())
        };
    }

    _incidentRangeTitle(incident) {
        // Short labels: the full alarm titles get clipped in the picker's ranges column.
        const names = { 12: 'Theft', 26: 'Perimeter Breach', 45: 'Tamper' };
        return `${names[incident.alarmnum] || 'Incident'} · ${incident.start.format('MMM D')}`;
    }

    _initCalendar() {
        if (this.calendarSelect || typeof $ === 'undefined' || !$.fn.calentim) return;
        // Calentim's range clicks call moment().middleOfDay(), which isn't a real
        // moment method; the portal polyfills it elsewhere, but the standalone
        // external page does not - without it every preset click throws before
        // onrangeselect fires.
        if (typeof moment.fn.middleOfDay !== 'function') {
            moment.fn.middleOfDay = function () {
                return this.hours(12).minutes(0).seconds(0).milliseconds(0);
            };
        }
        const button = this.querySelector('.stm-range-button');
        const dummy = this.querySelector('.stm-range-dummy');
        if (!button || !dummy) return;

        // Incident presets first, then the standard relative ranges.
        const ranges = this.incidents.map(inc => {
            const r = this._incidentRange(inc);
            return { title: this._incidentRangeTitle(inc), startDate: r.start, endDate: r.end };
        });
        ranges.push(
            { title: 'Last 6 hours', startDate: moment().subtract(6, 'hours'), endDate: moment() },
            { title: 'Last 24 hours', startDate: moment().subtract(24, 'hours'), endDate: moment() },
            { title: 'Last 7 days', startDate: moment().subtract(7, 'days'), endDate: moment() },
            { title: 'Last 30 days', startDate: moment().subtract(30, 'days'), endDate: moment() }
        );

        // Default selection: the most recent incident, otherwise today so far.
        const def = this.incidents.length
            ? this._incidentRange(this.incidents[0])
            : { start: moment().startOf('day'), end: moment() };

        const that = this;
        this.calendarSelect = $(button).calentim({
            classes: 'stm-calendar',
            singleDate: false,
            calendarCount: 1,
            showHeader: false,
            showFooter: true,
            autoCloseOnSelect: true,
            showTimePickers: true,
            showOn: 'bottom',
            format: 'MMM D, YYYY h:mm A',
            maxDate: moment(),
            startDate: def.start,
            endDate: def.end,
            target: $(dummy),
            rangeOrientation: 'vertical',
            autoAlign: true,
            ranges,
            onafterselect: function (elem, start, end) {
                that._onRangeSelected(start, end);
            },
            onrangeselect: function (elem, range) {
                that._onRangeSelected(range.startDate, range.endDate);
            }
        }).data('calentim');
    }

    _onRangeSelected(start, end) {
        let s = moment(start);
        let e = moment(end);
        // Double-clicking a single date leaves both timeslots at the same moment;
        // treat that as "show me that whole day" (capped at now).
        if (s.isSame(e)) {
            s = s.clone().startOf('day');
            e = moment.min(s.clone().endOf('day'), moment());
        }
        this.mode = 'historical';
        this.historicalRange = { start: s, end: e };
        this._hideLiveMarker();
        this._loadTrack(s, e);
    }

    /* ------------------------------------------------------------------ */
    /* External sharable link                                             */
    /* ------------------------------------------------------------------ */

    _updateExternalLink(status) {
        if (this.external || !status.token) return;
        this.token = status.token;
        const imperial = DplLink.currentUser.configoptions.USEIMPERIAL === 't';
        this.externallink = `${window.location.origin}/externalmap.html?token=${this.token}&adminid=${DplLink.currentCompany.companyid}&machineid=${this.device}&imperial=${imperial}`;
        this.externallinkenabled = status.tokenenabled;
    }

    onEnableLink(event) {
        event.stopPropagation();
        DplLink.apiCall({
            url: `${DplLink.resthost}${DplLink.apiUrls.toggleexternallink}`,
            type: 'PUT',
            data: {
                p1: this.token,
                p2: parseInt(this.device, 10),
                p3: parseInt(DplLink.currentCompany.companyid, 10),
                p4: parseInt(1 - this.externallinkenabled, 10)
            },
            success: () => {
                if (this.externallinkenabled === 1) {
                    this.externallinkenabled = 0;
                    DplLink.NetworkMsgPopup.show('Link Disabled', 'success', 2000);
                } else {
                    this.externallinkenabled = 1;
                    DplLink.copyToClipboard(this.externallink);
                    DplLink.NetworkMsgPopup.show('Link Enabled and Copied to Clipboard', 'success', 2000);
                }
            },
            error: () => {
                DplLink.NetworkMsgPopup.show('Problem Enabling Link', 'error', 2000);
            }
        });
    }

    onCopyLink(event) {
        event.stopPropagation();
        if (this.externallinkenabled === 1) {
            DplLink.copyToClipboard(this.externallink);
            DplLink.NetworkMsgPopup.show('Link Copied', 'success', 2000);
        } else {
            DplLink.NetworkMsgPopup.show('Link Not Enabled', 'error', 2000);
        }
    }

    onOpenLink(event) {
        event.stopPropagation();
        if (this.externallinkenabled === 1) {
            window.open(this.externallink, '_blank');
        } else {
            DplLink.NetworkMsgPopup.show('Link Not Enabled', 'error', 2000);
        }
    }

    /* ------------------------------------------------------------------ */
    /* Render                                                             */
    /* ------------------------------------------------------------------ */

    _renderPlaybackHud() {
        if (this.mode === 'live' || this.points.length < 2) return nothing;
        const clock = this._formatDeviceTime(this._trackStartMs + this.playbackPos);

        // While a trim handle is being dragged, the edge labels track the handle
        // in real time so you can see the time you're trimming to.
        const win = this._timelineWindow();
        const winWidth = win.end - win.start;
        const labelStartMs = this._trim && this._trim.side === 'l'
            ? win.start + this._trim.frac * winWidth : win.start;
        const labelEndMs = this._trim && this._trim.side === 'r'
            ? win.start + this._trim.frac * winWidth : win.end;
        return html`
            <div class="stm-playback">
                <div class="stm-transport">
                    <button class="stm-btn stm-play-btn" @click=${this.onTogglePlay} title="${this.isPlaying ? 'Pause' : 'Play'} route playback">
                        ${this.isPlaying ? '❚❚' : '►'}
                    </button>
                    <button class="stm-btn stm-speed-btn" @click=${this.onCycleSpeed} title="Playback speed">${this.playbackSpeed}x</button>
                    <div class="stm-clock" title="Device local time at the playback position">
                        <span class="stm-clock-time">${clock.time}</span>
                        <span class="stm-clock-meta">${clock.tz ? `${clock.tz} · ` : ''}${clock.date}</span>
                    </div>
                </div>
                <div class="stm-timeline">
                    <span class="stm-win-label ${this._trim && this._trim.side === 'l' ? 'stm-win-label-active' : ''}">${this._winLabel(labelStartMs)}</span>
                    <div class="stm-tl" title="Drag to scrub. Scroll to zoom. Drag the edge handles to trim the playback range."
                        @pointerdown=${this.onTlPointerDown} @pointermove=${this.onTlPointerMove}
                        @pointerup=${this.onTlPointerUp} @pointercancel=${this.onTlPointerUp}
                        @wheel=${this.onTimelineWheel}>
                        <canvas class="stm-tl-canvas"></canvas>
                        <div class="stm-tl-fill" style="width:${(this._playheadFrac() * 100).toFixed(3)}%"></div>
                        <div class="stm-tl-playhead" style="left:${(this._playheadFrac() * 100).toFixed(3)}%"></div>
                        ${this._trim ? html`<div class="stm-tl-shade" style="${this._trim.side === 'l'
                            ? `left:0;width:${(this._trim.frac * 100).toFixed(3)}%`
                            : `left:${(this._trim.frac * 100).toFixed(3)}%;right:0`}"></div>` : ''}
                        <div class="stm-tl-handle stm-tl-handle-l"
                            title="Drag right to trim the start of the playback range"
                            style="left:${(((this._trim && this._trim.side === 'l') ? this._trim.frac : 0) * 100).toFixed(3)}%"
                            @pointerdown=${(e) => this.onTrimDown(e, 'l')} @pointermove=${this.onTrimMove}
                            @pointerup=${this.onTrimUp} @pointercancel=${this.onTrimUp}></div>
                        <div class="stm-tl-handle stm-tl-handle-r"
                            title="Drag left to trim the end of the playback range"
                            style="left:calc(${(((this._trim && this._trim.side === 'r') ? this._trim.frac : 1) * 100).toFixed(3)}% - 10px)"
                            @pointerdown=${(e) => this.onTrimDown(e, 'r')} @pointermove=${this.onTrimMove}
                            @pointerup=${this.onTrimUp} @pointercancel=${this.onTrimUp}></div>
                    </div>
                    <span class="stm-win-label ${this._trim && this._trim.side === 'r' ? 'stm-win-label-active' : ''}">${this._winLabel(labelEndMs)}</span>
                    <div class="stm-zoom">
                        <button class="stm-btn stm-zoom-btn" @click=${() => this.onTimelineZoom(2)} title="Zoom in on the timeline">+</button>
                        <button class="stm-btn stm-zoom-btn" @click=${() => this.onTimelineZoom(0.5)} title="Zoom out">&minus;</button>
                        <button class="stm-btn stm-zoom-btn" @click=${this.onZoomReset} title="Show the entire range"
                            style="visibility:${this._winStartMs !== null ? 'visible' : 'hidden'}">&#10226;</button>
                    </div>
                </div>
            </div>
        `;
    }

    // Current playback point stats, shown in the legend row above the map so
    // they never collide with the timeline controls.
    _renderPlaybackStats() {
        if (this.points.length < 2) return nothing;
        const p = this.points[Math.min(this.playbackIndex, this.points.length - 1)];
        if (!p) return nothing;
        const imperial = !!(DplLink.currentUser && DplLink.currentUser.configoptions &&
            DplLink.currentUser.configoptions.USEIMPERIAL !== 'f');
        return html`
            <span class="stm-playback-info">
                <span>${p._stop ? 'Stop' : (p.direction === 11 ? 'WiFi' : 'GPS')}</span>
                <span>${p._stop ? '' : (p.speed != null ? `${p.speed} ${imperial ? 'mph' : 'km/h'}` : '')}</span>
                <span>${p._stop ? '' : this._getDirectionText(p.direction)}</span>
                <span>${this.playbackIndex + 1} / ${this.points.length}</span>
                ${this.pathMode === 'cleaned' && this.cleanStats && this.cleanStats.raw > this.cleanStats.cleaned ? html`
                    <span title="Cleaned from ${this.cleanStats.raw} raw points (${this.cleanStats.stops} stops)">${this.cleanStats.raw} raw</span>
                ` : nothing}
            </span>
        `;
    }

    render() {
        // Hide rather than remove so the Google Map container survives state flips.
        return html`
            <style>
                security-tracking-map .stm-toolbar {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 6px;
                    padding: 6px 0;
                }
                security-tracking-map .stm-btn {
                    background-color: #fff;
                    border: 1px solid #ccc;
                    border-radius: 3px;
                    box-shadow: 0 1px 3px rgba(0,0,0,.2);
                    cursor: pointer;
                    padding: 4px 10px;
                    font-size: 12px;
                }
                security-tracking-map .stm-btn.stm-active {
                    background-color: #e7f0fa;
                    border-color: #2c7be5;
                    font-weight: bold;
                }
                security-tracking-map .stm-link-section {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                security-tracking-map .stm-link-btns {
                    display: flex;
                    gap: 8px;
                }
                security-tracking-map .stm-link-btns .stm-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                }
                security-tracking-map .stm-seg {
                    display: inline-flex;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0,0,0,.2);
                }
                security-tracking-map .stm-seg-btn {
                    background-color: #fff;
                    border: none;
                    border-right: 1px solid #ccc;
                    cursor: pointer;
                    padding: 4px 12px;
                    font-size: 12px;
                }
                security-tracking-map .stm-seg-btn:last-child { border-right: none; }
                security-tracking-map .stm-seg-btn.stm-seg-active {
                    background-color: #2c7be5;
                    color: #fff;
                    font-weight: bold;
                }
                security-tracking-map .stm-opts {
                    display: flex;
                    gap: 6px;
                    margin-left: auto;
                }
                security-tracking-map .stm-map-center-btn {
                    background-color: #fff;
                    border: none;
                    border-radius: 2px;
                    box-shadow: 0 1px 4px rgba(0,0,0,.3);
                    width: 34px;
                    height: 34px;
                    margin: 0 10px 10px 0;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                security-tracking-map .stm-map-center-btn:hover { background-color: #f5f5f5; }
                security-tracking-map .stm-warn-banner {
                    background-color: #fff8e1;
                    border: 1px solid #f7ce46;
                    border-radius: 4px;
                    font-size: 12px;
                    margin: 4px 0;
                    padding: 6px 10px;
                }
                security-tracking-map .stm-playback {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    row-gap: 6px;
                    padding: 6px 0;
                    flex-wrap: wrap;
                    min-width: 0;
                }
                security-tracking-map .stm-transport {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex: 0 0 auto;
                }
                security-tracking-map .stm-timeline {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    /* big enough basis that it wraps to its own full-width row on
                       narrow cards instead of squeezing into leftover space */
                    flex: 1 1 320px;
                    min-width: 0;
                }
                security-tracking-map .stm-tl {
                    position: relative;
                    flex: 1;
                    height: 26px;
                    min-width: 120px;
                    background-color: #f1f3f5;
                    border: 1px solid #d7dbdf;
                    border-radius: 4px;
                    cursor: ew-resize;
                    overflow: hidden;
                    touch-action: none; /* allow pointer-drag scrubbing on touch */
                }
                security-tracking-map .stm-tl-canvas {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    display: block;
                }
                security-tracking-map .stm-tl-fill {
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    background-color: rgba(44,123,229,.12);
                    pointer-events: none;
                }
                security-tracking-map .stm-tl-shade {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    background-color: rgba(33,37,41,.28);
                    pointer-events: none;
                    z-index: 2;
                }
                security-tracking-map .stm-tl-handle {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 10px;
                    background-color: #2c7be5;
                    cursor: col-resize;
                    touch-action: none;
                    z-index: 3;
                    opacity: .9;
                }
                security-tracking-map .stm-tl-handle::after {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    width: 2px;
                    height: 12px;
                    background-color: #fff;
                    border-radius: 1px;
                    transform: translate(-50%, -50%);
                }
                security-tracking-map .stm-tl-handle-l {
                    border-radius: 4px 0 0 4px;
                }
                security-tracking-map .stm-tl-handle-r {
                    border-radius: 0 4px 4px 0;
                }
                security-tracking-map .stm-tl-playhead {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    margin-left: -1px;
                    background-color: #d9534f;
                    box-shadow: 0 0 3px rgba(217,83,79,.8);
                    pointer-events: none;
                }
                security-tracking-map .stm-win-label {
                    font-size: 10px;
                    color: #777;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                    flex: 0 0 92px; /* fixed so digit changes don't shift the strip */
                    overflow: hidden;
                    text-align: center;
                }
                security-tracking-map .stm-win-label-active {
                    color: #2c7be5;
                    font-weight: 600;
                }
                security-tracking-map .stm-zoom { display: flex; gap: 3px; }
                security-tracking-map .stm-zoom-btn {
                    padding: 2px 7px;
                    font-size: 12px;
                    line-height: 1.3;
                }
                security-tracking-map .stm-clock {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    background: #2b3035;
                    color: #fff;
                    border-radius: 4px;
                    padding: 4px 10px;
                    line-height: 1.2;
                    flex: none;
                }
                security-tracking-map .stm-clock-time {
                    font-family: 'SF Mono', Menlo, Consolas, monospace;
                    font-size: 12px;
                    font-weight: 600;
                    font-variant-numeric: tabular-nums;
                    white-space: nowrap;
                    min-width: 11ch; /* widest case "12:34:56 PM"; stops layout shift */
                    text-align: center;
                }
                security-tracking-map .stm-clock-meta {
                    font-size: 10px;
                    color: #b8c0c8;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                    min-width: 14ch; /* fits "EDT · Jun 10"; day digit changes don't resize the pill */
                    text-align: center;
                }
                security-tracking-map .stm-playback-info {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                    margin-left: auto;
                    color: #555;
                }
                security-tracking-map .stm-playback-info span {
                    white-space: nowrap;
                }
                security-tracking-map .stm-legend-stop {
                    background: rgba(92,107,122,.35);
                    border: 1px solid #5c6b7a;
                    border-radius: 50%;
                    width: 12px;
                    height: 12px;
                    display: inline-block;
                    vertical-align: -2px;
                }
                security-tracking-map .stm-playback-dot {
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background-color: #d9534f;
                    border: 3px solid #fff;
                    box-shadow: 0 0 6px rgba(217,83,79,.9);
                    transform: translateY(50%); /* center on the coordinate */
                }
                security-tracking-map .stm-pov-wrap {
                    position: relative;
                    transform: translateY(50%); /* center on the coordinate */
                    pointer-events: none;
                }
                security-tracking-map .stm-pov-rot {
                    position: relative;
                    width: 72px;
                    height: 72px;
                    transition: transform .15s linear;
                }
                /* Pegman-orange so it reads as the street view camera, not "my location" */
                security-tracking-map .stm-pov-cone {
                    position: absolute;
                    left: 50%;
                    top: 4px;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 16px solid transparent;
                    border-right: 16px solid transparent;
                    border-top: 30px solid rgba(251,140,0,.2);
                }
                security-tracking-map .stm-pov-cam {
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%,-50%);
                    opacity: .55;
                    filter: drop-shadow(0 0 1px rgba(255,255,255,.9)) drop-shadow(0 1px 2px rgba(0,0,0,.35));
                    line-height: 0;
                }
                /* Let the info window size to its content; never show inner scrollbars */
                security-tracking-map .gm-style-iw-d {
                    overflow: hidden !important;
                }
                security-tracking-map .stm-iw {
                    font-family: inherit;
                    max-width: 250px;
                    line-height: 1.45;
                    padding: 4px 12px 6px 2px;
                }
                security-tracking-map .stm-iw-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-bottom: 4px;
                }
                security-tracking-map .stm-iw-title {
                    font-size: 13px;
                    font-weight: 600;
                    color: #222;
                }
                security-tracking-map .stm-iw-sub {
                    font-weight: normal;
                    color: #888;
                    font-size: 11px;
                    margin-left: 6px;
                }
                security-tracking-map .stm-iw-src {
                    font-size: 10px;
                    font-weight: bold;
                    border-radius: 8px;
                    padding: 2px 8px;
                    color: #fff;
                    flex: none;
                }
                security-tracking-map .stm-iw-src-gps { background: ${GPS_PATH_COLOR}; }
                security-tracking-map .stm-iw-src-wifi { background: ${WIFI_PATH_COLOR}; }
                security-tracking-map .stm-iw-src-cell { background: #c9a227; }
                security-tracking-map .stm-iw-addr {
                    font-size: 12px;
                    color: #333;
                    margin: 2px 0;
                }
                security-tracking-map .stm-iw-meta {
                    display: flex;
                    gap: 8px;
                    font-size: 11px;
                    color: #666;
                    margin: 2px 0;
                }
                security-tracking-map .stm-iw-ago { color: #999; }
                security-tracking-map .stm-iw-foot {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    margin-top: 2px;
                }
                security-tracking-map .stm-iw-coords {
                    font-size: 10px;
                    color: #aaa;
                    font-family: monospace;
                }
                security-tracking-map .stm-iw-copy {
                    background: none;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    cursor: pointer;
                    padding: 1px 4px;
                    line-height: 0;
                    flex: none;
                }
                security-tracking-map .stm-iw-copy:hover { background: #f2f6fc; border-color: #2c7be5; }
                security-tracking-map .stm-iw-copy .copyIcon {
                    width: 14px;
                    height: 14px;
                    background-size: 14px 14px;
                    margin: 0;
                }
                security-tracking-map .stm-iw-rows { font-size: 12px; }
                security-tracking-map .stm-iw-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 14px;
                    padding: 1px 0;
                }
                security-tracking-map .stm-iw-row span:first-child { color: #888; }
                security-tracking-map .stm-range-dummy { display: none; }
                security-tracking-map .stm-device-range {
                    font-size: 11px;
                    color: #777;
                    padding: 2px 0;
                }
                security-tracking-map .stm-map-wrap {
                    display: flex;
                    gap: 8px;
                }
                security-tracking-map .stm-map-wrap .stm-map {
                    flex: 1;
                    min-width: 0;
                }
                security-tracking-map .stm-map-wrap .stm-pano-wrap {
                    display: none;
                    flex: 1;
                    min-width: 0;
                    position: relative;
                    min-height: 500px;
                }
                security-tracking-map .stm-map-wrap.stm-split .stm-pano-wrap {
                    display: block;
                }
                security-tracking-map .stm-pano-wrap .stm-pano {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    margin: 0;
                    transition: opacity .4s ease;
                    background: #1a1a1a;
                }
                security-tracking-map .stm-pano-wrap .stm-pano.stm-pano-front {
                    opacity: 1;
                    z-index: 2;
                    pointer-events: auto;
                }
                security-tracking-map .stm-pano-wrap .stm-pano.stm-pano-back {
                    opacity: 0;
                    z-index: 1;
                    pointer-events: none;
                }
                security-tracking-map .stm-pano-msg {
                    position: absolute;
                    inset: 0;
                    z-index: 3;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    padding: 12px;
                    /* Translucent so the last good Street View stays visible underneath. */
                    background-color: rgba(241, 243, 245, .82);
                    color: #667;
                    font-size: 13px;
                }
                @media (max-width: 800px) {
                    /* Stacked split: map and street view each take half the full-view
                       map height, so the split fits in the same vertical space. */
                    security-tracking-map .stm-map-wrap.stm-split {
                        flex-direction: column;
                        gap: 6px;
                    }
                    security-tracking-map .stm-map-wrap.stm-split .stm-map,
                    security-tracking-map .stm-map-wrap.stm-split .stm-pano-wrap {
                        height: 250px;
                        min-height: 250px;
                        margin: 0;
                    }
                    security-tracking-map .stm-win-label {
                        flex: 0 0 78px;
                        font-size: 9px;
                    }
                }
                security-tracking-map .stm-loading {
                    font-size: 12px;
                    color: #777;
                    padding: 2px 6px;
                }
                security-tracking-map .stm-legend {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                    font-size: 11px;
                    color: #555;
                    padding: 2px 0;
                }
                security-tracking-map .stm-legend-swatch {
                    display: inline-block;
                    width: 18px;
                    height: 4px;
                    border-radius: 2px;
                    margin-right: 4px;
                    vertical-align: middle;
                }

                /* Dark theme: recolor the white control chips (they set no text
                   color, so on dark they inherit light text on a white face and
                   vanish). Active/selected accent blue is preserved. */
                html[data-theme="dark"] security-tracking-map .stm-btn,
                html[data-theme="dark"] security-tracking-map .stm-seg-btn {
                    background-color: var(--dpl-surface-raised);
                    color: var(--dpl-text);
                    border-color: var(--dpl-border);
                }
                html[data-theme="dark"] security-tracking-map .stm-seg { border-color: var(--dpl-border); }
                html[data-theme="dark"] security-tracking-map .stm-seg-btn { border-right-color: var(--dpl-border); }
                html[data-theme="dark"] security-tracking-map .stm-btn.stm-active {
                    background-color: var(--dpl-surface-alt);
                    border-color: #2c7be5;
                    color: var(--dpl-text);
                }
                html[data-theme="dark"] security-tracking-map .stm-seg-btn.stm-seg-active {
                    background-color: #2c7be5;
                    color: #fff;
                }
                html[data-theme="dark"] security-tracking-map .stm-map-center-btn { background-color: var(--dpl-surface-raised); }
                html[data-theme="dark"] security-tracking-map .stm-map-center-btn:hover { background-color: var(--dpl-surface-alt); }
                html[data-theme="dark"] security-tracking-map .stm-legend { color: var(--dpl-text-muted); }
                /* Timeline scrubber strip + labels */
                html[data-theme="dark"] security-tracking-map .stm-tl {
                    background-color: var(--dpl-surface-alt);
                    border-color: var(--dpl-border);
                }
                html[data-theme="dark"] security-tracking-map .stm-tl-fill {
                    background-color: rgba(100, 170, 255, 0.18);
                }
                html[data-theme="dark"] security-tracking-map .stm-tl-shade {
                    background-color: rgba(0, 0, 0, 0.45);
                }
                html[data-theme="dark"] security-tracking-map .stm-win-label {
                    color: var(--dpl-text-muted);
                }
                html[data-theme="dark"] security-tracking-map .stm-win-label-active {
                    color: #8ab4ff;
                }
                html[data-theme="dark"] security-tracking-map .stm-playback-info {
                    color: var(--dpl-text-muted);
                }
                html[data-theme="dark"] security-tracking-map .stm-warn-banner {
                    background-color: rgba(184, 134, 11, 0.22);
                    border-color: rgba(255, 224, 130, 0.45);
                    color: var(--dpl-text);
                }
                html[data-theme="dark"] security-tracking-map .stm-device-range {
                    color: var(--dpl-text-muted);
                }
                /* InfoWindow bubble (appears on marker hover) — Google defaults to white */
                html[data-theme="dark"] security-tracking-map .gm-style-iw-c {
                    background-color: var(--dpl-surface) !important;
                    box-shadow: var(--dpl-shadow-card) !important;
                    color: var(--dpl-text);
                }
                html[data-theme="dark"] security-tracking-map .gm-style-iw-d {
                    color: var(--dpl-text);
                }
                html[data-theme="dark"] security-tracking-map .gm-style-iw-t::after,
                html[data-theme="dark"] security-tracking-map .gm-style-iw-tc::after {
                    background: var(--dpl-surface) !important;
                }
                html[data-theme="dark"] security-tracking-map .stm-iw-title { color: var(--dpl-text); }
                html[data-theme="dark"] security-tracking-map .stm-iw-sub,
                html[data-theme="dark"] security-tracking-map .stm-iw-ago,
                html[data-theme="dark"] security-tracking-map .stm-iw-coords,
                html[data-theme="dark"] security-tracking-map .stm-iw-row span:first-child {
                    color: var(--dpl-text-muted);
                }
                html[data-theme="dark"] security-tracking-map .stm-iw-addr,
                html[data-theme="dark"] security-tracking-map .stm-iw-meta,
                html[data-theme="dark"] security-tracking-map .stm-iw-row {
                    color: var(--dpl-text-secondary);
                }
                html[data-theme="dark"] security-tracking-map .stm-iw-copy {
                    border-color: var(--dpl-border);
                    background-color: var(--dpl-surface-raised);
                }
                html[data-theme="dark"] security-tracking-map .stm-iw-copy:hover {
                    background-color: var(--dpl-surface-alt);
                    border-color: #2c7be5;
                }
            </style>
            <div class="card gpsTracking" style="display:${this.hiddenCard ? 'none' : 'block'}">
                <div class="groupHeader">Location</div>

                ${this.truncatedTrack ? html`
                    <div class="stm-warn-banner">Track was limited to 1000 points &mdash; select a narrower time range to see full detail.</div>
                ` : nothing}

                <div class="stm-toolbar">
                    <div class="stm-seg">
                        <button class="stm-seg-btn ${this.mode === 'live' ? 'stm-seg-active' : ''}" @click=${this.onBackToLive} title="Return to live location">Live</button>
                        <button class="stm-seg-btn stm-range-button ${this.mode === 'historical' ? 'stm-seg-active' : ''}" @click=${this.onHistoryClick} title="Select a time period to view the path the device took">
                            ${this.mode === 'historical' && this.historicalRange
                                ? `${this.historicalRange.start.format('MMM D h:mm A')} - ${this.historicalRange.end.format('MMM D h:mm A')}`
                                : 'History...'}
                        </button>
                    </div>
                    <input class="stm-range-dummy" type="text" />
                    ${this.loadingTrack ? html`<span class="stm-loading">Loading track...</span>` : nothing}
                    <div class="stm-opts">
                        <button class="stm-btn ${this.showTowers ? 'stm-active' : ''}" @click=${this.onToggleTowers} title="Show the cell towers used while reporting tracking data">Cell Towers</button>
                        ${!this.cellOnly ? html`
                            <button class="stm-btn ${this.streetViewOn ? 'stm-active' : ''}" @click=${this.onStreetView} title="Toggle split-screen Google Street View">Street View</button>
                        ` : nothing}
                        ${this.mode === 'historical' && this.rawPoints.length > 0 ? html`
                            <button class="stm-btn ${this.pathMode === 'cleaned' ? 'stm-active' : ''}"
                                @click=${this.onTogglePathMode}
                                title="${this.pathMode === 'cleaned'
                                    ? 'Showing cleaned path (tilt/motion-gated, WiFi-anchored stops). Click for raw GPS/WiFi points.'
                                    : 'Showing raw GPS/WiFi points. Click for cleaned path.'}">
                                ${this.pathMode === 'cleaned' ? 'Cleaned Path' : 'Raw Path'}
                            </button>
                            <button class="stm-btn" @click=${this.onExportCsv}
                                title="Export lat/lon/speed/time for the visible History range as CSV">
                                Export CSV
                            </button>
                        ` : nothing}
                    </div>
                </div>

                ${this.mode === 'historical' && this._deviceRangeNote() ? html`
                    <div class="stm-device-range">Device local time: ${this._deviceRangeNote()}</div>
                ` : nothing}

                ${this.mode !== 'live' && this.points.length > 0 ? html`
                    <div class="stm-legend">
                        <span><span class="stm-legend-swatch" style="background:${GPS_PATH_COLOR}"></span>GPS path</span>
                        <span><span class="stm-legend-swatch" style="background:${WIFI_PATH_COLOR}"></span>WiFi path</span>
                        ${this.pathMode === 'cleaned' ? html`
                            <span><span class="stm-legend-swatch stm-legend-stop"></span>Stop</span>
                        ` : nothing}
                        ${this._renderPlaybackStats()}
                    </div>
                ` : nothing}

                <div class="stm-map-wrap ${this.streetViewOn ? 'stm-split' : ''}">
                    <div class="stm-map mapHolder"></div>
                    <div class="stm-pano-wrap">
                        <div class="stm-pano stm-pano-a stm-pano-front mapHolder"></div>
                        <div class="stm-pano stm-pano-b stm-pano-back mapHolder"></div>
                        ${!this.panoAvailable ? html`
                            <div class="stm-pano-msg">Street View is not available for this point</div>
                        ` : nothing}
                    </div>
                </div>

                ${this._renderPlaybackHud()}

                ${!this.external ? html`
                    <div class="linkSection stm-link-section">
                        <div class="enabledctn" style="display: flex;" @click=${this.onEnableLink}>
                            <label class="inlineLabelLong" style="padding-left: 5px;margin-top: 8px;font-weight: normal;width: 175px;"><div class="shareLinkIcon ${this.externallinkenabled === 1 ? 'on' : ''}"></div>Sharable Link Enabled</label>
                            <div class="toggleSwitch" style="margin: 12px 10px;cursor: pointer;"><div class="toggle ${this.externallinkenabled === 1 ? 'on' : ''}"></div></div>
                        </div>
                        <div class="stm-link-btns">
                            <button class="stm-btn" @click=${this.onCopyLink} title="Copy the external tracking link"><div class="copyIcon"></div>Copy Link</button>
                            <button class="stm-btn" @click=${this.onOpenLink} title="Open the external tracking page"><div class="openLinkIcon"></div>Open Link</button>
                        </div>
                    </div>
                ` : nothing}
            </div>
        `;
    }

    updated() {
        // The map container only exists after the card is shown; safe to call repeatedly.
        if (!this.map && !this.hiddenCard && this.status) {
            this._initMap();
        }
        if (this.mode !== 'live' && this.points.length > 1) {
            this._drawTimelineCanvas(); // no-op unless window/points/size changed
        }
    }
}

customElements.define('security-tracking-map', SecurityTrackingMap);
export { SecurityTrackingMap };
